/**
 * ss-pdp-main-banner.js
 * Web Component powering the "mattress PDP main banner" section
 * (sections/ss-pdp-main-banner.liquid) for the Jarvis Technology theme.
 *
 * Everything is scoped to the <ss-pdp-main-banner> element. All texts, ids,
 * endpoints and keys are read from the JSON config rendered by Liquid:
 *   <script type="application/json" data-ss-pdp-config>
 *   <script type="application/json" data-ss-pdp-variants>
 *   <script type="application/json" data-ss-pdp-custom-variants>  (optional)
 */
(function () {
  'use strict';

  /* BCP-47 tag for Intl: Shopify's locale ("en") plus the market country ("GB")
     gives region-correct formats (en-GB → 16:00, de-CH → CHF 1'234.50). */
  function ssLocaleTag(config) {
    var locale = String((config && config.locale) || '').trim();
    var country = String((config && config.country) || '').trim().toUpperCase();
    if (!locale) return undefined;
    if (locale.indexOf('-') === -1 && /^[A-Z]{2}$/.test(country)) return locale + '-' + country;
    return locale;
  }

  /* The theme declares `const PUB_SUB_EVENTS` in pubsub.js — a lexical global,
     not a window property — so resolve it lazily by identifier first. */
  function ssPubSubEvents() {
    try { if (typeof PUB_SUB_EVENTS !== 'undefined') return PUB_SUB_EVENTS; } catch (e) { /* noop */ }
    return ssPubSubEvents() || null;
  }
  function ssPublish(eventName, payload) {
    try { if (typeof publish === 'function') return publish(eventName, payload); } catch (e) { /* noop */ }
    if (typeof window.publish === 'function') return ssPublish(eventName, payload);
    return undefined;
  }
  function ssSubscribe(eventName, callback) {
    try { if (typeof subscribe === 'function') return subscribe(eventName, callback); } catch (e) { /* noop */ }
    if (typeof window.subscribe === 'function') return ssSubscribe(eventName, callback);
    return null;
  }

  if (window.customElements && customElements.get('ss-pdp-main-banner')) return;

  var LOG_PREFIX = '[ss-pdp]';
  var REQUEST_TIMEOUT_MS = 10000;
  var MAP_CONSENT_KEY = 'ssPdpMapConsent';
  var WISHLIST_LOCAL_KEY = 'ssPdpWishlist';
  var WISHLIST_PENDING_KEY = 'wishlist-product';
  var WISHLIST_SYNC_COOKIE = 'wishlist_synced';
  var CM_PER_IN = 2.54;
  var IN_PER_FT = 12;

  function warn() {
    var args = Array.prototype.slice.call(arguments);
    args.unshift(LOG_PREFIX);
    try { console.warn.apply(console, args); } catch (e) { /* noop */ }
  }

  /* ------------------------------------------------------------------ */
  /* Generic helpers                                                     */
  /* ------------------------------------------------------------------ */

  function isObject(v) { return v !== null && typeof v === 'object'; }

  function get(obj, path, fallback) {
    var parts = String(path).split('.');
    var cur = obj;
    for (var i = 0; i < parts.length; i++) {
      if (!isObject(cur) || !(parts[i] in cur)) return fallback;
      cur = cur[parts[i]];
    }
    return cur === undefined || cur === null ? fallback : cur;
  }

  function toNumber(value, fallback) {
    var n = Number(value);
    return Number.isFinite(n) ? n : (fallback === undefined ? 0 : fallback);
  }

  function escapeHtml(value) {
    return String(value === undefined || value === null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function escapeAttr(value) { return escapeHtml(value); }

  function safeUrl(value) {
    var str = String(value || '').trim();
    if (!str) return '';
    if (/^(https?:)?\/\//i.test(str) || /^(tel|mailto):/i.test(str) || str.charAt(0) === '/') return str;
    return '';
  }

  function show(el, visible) {
    if (!el) return;
    if (visible === false) {
      el.setAttribute('hidden', '');
      el.classList.add('hidden');
    } else {
      el.removeAttribute('hidden');
      el.classList.remove('hidden');
      if (el.style && el.style.display === 'none') el.style.display = '';
    }
  }

  function hide(el) { show(el, false); }

  function isVisible(el) {
    return !!el && !el.hasAttribute('hidden') && !el.classList.contains('hidden');
  }

  function prefersReducedMotion() {
    try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) { return false; }
  }

  function isSmallScreen() {
    if (window.theme && window.theme.config && typeof window.theme.config.mqlSmall === 'boolean') {
      return window.theme.config.mqlSmall;
    }
    try { return window.matchMedia('screen and (max-width: 749px)').matches; } catch (e) { return window.innerWidth < 750; }
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function getUrlParameter(name) {
    try { return new URLSearchParams(window.location.search).get(name) || ''; } catch (e) { return ''; }
  }

  function parseJsonScript(scriptEl, fallback) {
    if (!scriptEl) return fallback;
    try {
      var text = (scriptEl.textContent || '').trim();
      return text ? JSON.parse(text) : fallback;
    } catch (e) {
      warn('Invalid JSON in', scriptEl.getAttribute('type'), e);
      return fallback;
    }
  }

  /* ------------------------------------------------------------------ */
  /* Strings: ssT(key, vars)                                             */
  /* ------------------------------------------------------------------ */

  /* Shopify's `t` filter HTML-escapes translations; decode exactly the five entities it emits. */
  function ssUnescape(text) {
    if (typeof text !== 'string' || text.indexOf('&') === -1) return text;
    return text.replace(/&(#39|quot|lt|gt|amp);/g, function (m, e) {
      return { '#39': "'", quot: '"', lt: '<', gt: '>', amp: '&' }[e];
    });
  }

  function createTranslator(strings) {
    var dict = isObject(strings) ? strings : {};
    return function ssT(key, vars) {
      var text = Object.prototype.hasOwnProperty.call(dict, key) && typeof dict[key] === 'string' ? ssUnescape(dict[key]) : key;
      if (vars && isObject(vars)) {
        text = text.replace(/\{\{\s*([\w.]+)\s*\}\}/g, function (m, name) {
          return Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : m;
        });
      }
      return text;
    };
  }

  /* ------------------------------------------------------------------ */
  /* Money / numbers / dates                                             */
  /* ------------------------------------------------------------------ */

  function createMoneyFormatter(config) {
    var format = config.moneyFormat || get(window, 'theme.shopSettings.moneyFormat', '') || '{{amount}}';
    return function formatMoney(cents) {
      var value = toNumber(cents, 0);
      try {
        if (window.theme && window.theme.Currency && typeof window.theme.Currency.formatMoney === 'function') {
          return window.theme.Currency.formatMoney(Math.round(value), format);
        }
      } catch (e) { /* fall through */ }
      try {
        return new Intl.NumberFormat(ssLocaleTag(config) || undefined, {
          style: 'currency',
          currency: config.currency || 'EUR'
        }).format(value / 100);
      } catch (e) {
        return (value / 100).toFixed(2);
      }
    };
  }

  function createNumberFormatter(locale) {
    var cache = {};
    return function formatNumber(value, maxFractionDigits) {
      var digits = maxFractionDigits === undefined ? 1 : maxFractionDigits;
      try {
        if (!cache[digits]) {
          cache[digits] = new Intl.NumberFormat(locale || undefined, { maximumFractionDigits: digits });
        }
        return cache[digits].format(value);
      } catch (e) {
        return String(Math.round(value * Math.pow(10, digits)) / Math.pow(10, digits));
      }
    };
  }

  function createDateFormatter(locale) {
    return function formatDate(date, opts) {
      var d = date instanceof Date ? date : new Date(date);
      if (isNaN(d.getTime())) return '';
      try {
        return new Intl.DateTimeFormat(locale || undefined, opts || { day: '2-digit', month: 'short' }).format(d);
      } catch (e) {
        return d.toDateString();
      }
    };
  }

  function toLocalDate(value) {
    if (value instanceof Date) return new Date(value.getTime());
    if (typeof value === 'string') {
      var m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    }
    var d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }

  function startOfDay(date) {
    var d = new Date(date.getTime());
    d.setHours(0, 0, 0, 0);
    return d;
  }

  function addBusinessDays(from, count) {
    var d = startOfDay(from);
    var added = 0;
    while (added < count) {
      d.setDate(d.getDate() + 1);
      var day = d.getDay();
      if (day !== 0 && day !== 6) added++;
    }
    return d;
  }

  function daysBetween(a, b) {
    return Math.round((startOfDay(b).getTime() - startOfDay(a).getTime()) / 86400000);
  }

  /**
   * Parses "11:00 AM", "11AM", "9:30PM", "10:00", "19:30" to minutes after midnight.
   * Returns { totalMinutes, hours, minutes } or null.
   */
  function parseClockTime(str) {
    if (!str) return null;
    var m = String(str).trim().match(/(\d{1,2})(?::(\d{2}))?\s*([AaPp]\.?\s*[Mm]\.?)?/);
    if (!m) return null;
    var hours = parseInt(m[1], 10);
    var minutes = m[2] ? parseInt(m[2], 10) : 0;
    if (isNaN(hours) || isNaN(minutes) || hours > 24 || minutes > 59) return null;
    var meridiem = m[3] ? m[3].replace(/[^APap]/g, '').toUpperCase() : '';
    if (meridiem === 'P' && hours !== 12) hours += 12;
    if (meridiem === 'A' && hours === 12) hours = 0;
    if (hours === 24) hours = 0;
    return { totalMinutes: hours * 60 + minutes, hours: hours, minutes: minutes };
  }

  /** Parses "10:00 - 11:00" / "11:00 AM - 12:00 PM" / "10:00–19:00" / "10 AM to 7 PM". */
  function parseTimeRange(str) {
    if (!str || typeof str !== 'string') return null;
    var m = str.match(/(\d{1,2}(?::\d{2})?\s*(?:[AaPp]\.?\s*[Mm]\.?)?)\s*(?:to|-|–|—|bis|til|tot)\s*(\d{1,2}(?::\d{2})?\s*(?:[AaPp]\.?\s*[Mm]\.?)?)/);
    if (!m) return null;
    var open = parseClockTime(m[1]);
    var close = parseClockTime(m[2]);
    if (!open || !close) return null;
    return { open: open, close: close };
  }

  function formatClockTime(locale, timeObj) {
    if (!timeObj) return '';
    var d = new Date();
    d.setHours(timeObj.hours, timeObj.minutes, 0, 0);
    try {
      return new Intl.DateTimeFormat(locale || undefined, { hour: '2-digit', minute: '2-digit' }).format(d);
    } catch (e) {
      return String(timeObj.hours).padStart(2, '0') + ':' + String(timeObj.minutes).padStart(2, '0');
    }
  }

  /* ------------------------------------------------------------------ */
  /* Size parsing + unit conversion                                      */
  /* ------------------------------------------------------------------ */

  function parseSizeValue(value) {
    if (value === undefined || value === null) return null;
    var m = String(value).replace(/,/g, '.').match(/(\d+(?:\.\d+)?)\D+?(\d+(?:\.\d+)?)/);
    if (!m) return null;
    return { length: parseFloat(m[1]), width: parseFloat(m[2]) };
  }

  function convertLength(value, fromUnit, toUnit) {
    var from = String(fromUnit || 'in').toLowerCase();
    var to = String(toUnit || 'in').toLowerCase();
    if (!Number.isFinite(value)) return NaN;
    var inches = value;
    if (from === 'cm') inches = value / CM_PER_IN;
    else if (from === 'ft') inches = value * IN_PER_FT;
    if (to === 'cm') return inches * CM_PER_IN;
    if (to === 'ft') return inches / IN_PER_FT;
    return inches;
  }

  /* ------------------------------------------------------------------ */
  /* Storage (try/catch + TTL)                                           */
  /* ------------------------------------------------------------------ */

  function createStorage(kind) {
    function store() {
      try { return kind === 'session' ? window.sessionStorage : window.localStorage; } catch (e) { return null; }
    }
    return {
      get: function (key) {
        try {
          var s = store();
          if (!s) return null;
          var raw = s.getItem(key);
          if (raw === null) return null;
          var parsed;
          try { parsed = JSON.parse(raw); } catch (e) { return raw; }
          if (isObject(parsed) && parsed.__ssTtl !== undefined) {
            if (Date.now() > parsed.__ssTtl) { s.removeItem(key); return null; }
            return parsed.value;
          }
          return parsed;
        } catch (e) { return null; }
      },
      set: function (key, value, ttlMinutes) {
        try {
          var s = store();
          if (!s) return false;
          var payload = ttlMinutes ? { value: value, __ssTtl: Date.now() + ttlMinutes * 60000 } : value;
          s.setItem(key, JSON.stringify(payload));
          return true;
        } catch (e) { return false; }
      },
      remove: function (key) {
        try { var s = store(); if (s) s.removeItem(key); } catch (e) { /* noop */ }
      }
    };
  }

  var storage = createStorage('local');
  var sessionStore = createStorage('session');

  function getCookie(name) {
    try {
      var match = document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)');
      if (!match) return null;
      var raw = decodeURIComponent(match[2]);
      try { return JSON.parse(raw); } catch (e) { return raw; }
    } catch (e) { return null; }
  }

  function deleteCookie(name) {
    try { document.cookie = name + '=; max-age=0; path=/'; } catch (e) { /* noop */ }
  }

  /* ------------------------------------------------------------------ */
  /* Network                                                             */
  /* ------------------------------------------------------------------ */

  /**
   * Single fetch wrapper: timeout via AbortController, JSON parse guard,
   * refuses to run with an empty endpoint. Resolves { ok, status, data, text }.
   */
  function request(url, opts) {
    var options = Object.assign({}, opts || {});
    if (!url || typeof url !== 'string' || !url.trim()) {
      return Promise.reject(new Error('Endpoint not configured'));
    }
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = null;
    if (controller) {
      options.signal = controller.signal;
      timer = setTimeout(function () { controller.abort(); }, options.timeout || REQUEST_TIMEOUT_MS);
    }
    delete options.timeout;
    var parseAs = options.parse || 'json';
    delete options.parse;

    return fetch(url, options)
      .then(function (response) {
        if (options.mode === 'no-cors') return { ok: true, status: response.status || 0, data: null, text: '' };
        return response.text().then(function (text) {
          var data = null;
          if (parseAs === 'json' && text) {
            try { data = JSON.parse(text); } catch (e) { data = null; }
          }
          return { ok: response.ok, status: response.status, data: data, text: text };
        });
      })
      .finally(function () { if (timer) clearTimeout(timer); });
  }

  function jsonHeaders(extra) {
    return Object.assign({ 'Content-Type': 'application/json', 'Accept': 'application/json' }, extra || {});
  }

  /* ------------------------------------------------------------------ */
  /* Analytics                                                           */
  /* ------------------------------------------------------------------ */

  function createTracker(config) {
    var enabled = get(config, 'features.analytics', true) !== false;
    return function track(eventName, params) {
      if (!enabled || !eventName) return;
      var p = isObject(params) ? params : {};
      try {
        if (typeof window.gaEvent === 'function') {
          var legacy = Array.isArray(p.legacy) ? p.legacy.slice(0, 6) : [];
          while (legacy.length < 6) legacy.push('');
          window.gaEvent(eventName, legacy, false);
        }
      } catch (e) { /* noop */ }
      try {
        if (Array.isArray(window.dataLayer)) {
          var payload = Object.assign({ event: eventName }, p);
          delete payload.legacy;
          window.dataLayer.push(payload);
        }
      } catch (e) { /* noop */ }
      try {
        if (window.webengage && typeof window.webengage.track === 'function') {
          var we = Object.assign({}, p);
          delete we.legacy;
          window.webengage.track(eventName, we);
        }
      } catch (e) { /* noop */ }
    };
  }

  /* ------------------------------------------------------------------ */
  /* Body scroll lock (ref-counted) + layer stack (ESC / focus trap)     */
  /* ------------------------------------------------------------------ */

  var OVERLAY_BODY_CLASS = "ss-pdp-overlay-open";
  var scrollLock = (function () {
    var count = 0;
    return {
      lock: function () {
        count++;
        document.body.classList.add(OVERLAY_BODY_CLASS);
      },
      unlock: function () {
        count = Math.max(0, count - 1);
        if (count === 0) document.body.classList.remove(OVERLAY_BODY_CLASS);
      },
      reset: function () {
        count = 0;
        document.body.classList.remove(OVERLAY_BODY_CLASS);
      }
    };
  })();

  var FOCUSABLE = 'summary, a[href], button:not([disabled]), [tabindex]:not([tabindex^="-"]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), iframe';

  function getFocusable(container) {
    return Array.from(container.querySelectorAll(FOCUSABLE)).filter(function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });
  }

  /**
   * Keeps track of open overlay layers. ESC closes only the topmost layer.
   * Each layer traps Tab focus inside its container and returns focus on close.
   */
  function createLayerStack() {
    var layers = [];
    var bound = false;

    function onKeydown(event) {
      if (!layers.length) return;
      var top = layers[layers.length - 1];
      if (event.key === 'Escape' || event.key === 'Esc') {
        event.preventDefault();
        event.stopPropagation();
        if (typeof top.onClose === 'function') top.onClose();
        return;
      }
      if (event.key === 'Tab' && top.container && top.container.contains(document.activeElement)) {
        var focusable = getFocusable(top.container);
        if (!focusable.length) { event.preventDefault(); return; }
        var first = focusable[0], last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      } else if (event.key === 'Tab' && top.container && !top.container.contains(document.activeElement)) {
        var items = getFocusable(top.container);
        if (items.length) { event.preventDefault(); items[0].focus(); }
      }
    }

    function bind() {
      if (bound) return;
      document.addEventListener('keydown', onKeydown, true);
      bound = true;
    }
    function unbind() {
      if (!bound) return;
      document.removeEventListener('keydown', onKeydown, true);
      bound = false;
    }

    return {
      push: function (layer) {
        if (!layer || !layer.container) return;
        this.remove(layer.container);
        layer.returnFocus = layer.returnFocus || document.activeElement;
        layers.push(layer);
        bind();
        var target = layer.initialFocus || getFocusable(layer.container)[0] || layer.container;
        try {
          if (!layer.container.hasAttribute('tabindex') && target === layer.container) layer.container.setAttribute('tabindex', '-1');
          target.focus({ preventScroll: true });
        } catch (e) { /* noop */ }
      },
      remove: function (container) {
        var idx = -1;
        for (var i = layers.length - 1; i >= 0; i--) { if (layers[i].container === container) { idx = i; break; } }
        if (idx === -1) return;
        var layer = layers.splice(idx, 1)[0];
        if (!layers.length) unbind();
        try {
          if (layer.returnFocus && typeof layer.returnFocus.focus === 'function' && document.contains(layer.returnFocus)) {
            layer.returnFocus.focus({ preventScroll: true });
          }
        } catch (e) { /* noop */ }
      },
      has: function (container) {
        return layers.some(function (l) { return l.container === container; });
      },
      isTop: function (container) {
        return layers.length > 0 && layers[layers.length - 1].container === container;
      },
      clear: function () {
        while (layers.length) {
          var l = layers.pop();
          try { if (typeof l.onClose === 'function') l.onClose(true); } catch (e) { /* noop */ }
        }
        unbind();
      }
    };
  }

  /* ------------------------------------------------------------------ */
  /* Module base                                                         */
  /* ------------------------------------------------------------------ */

  class SsPdpModule {
    constructor(root) {
      this.root = root;
      this.config = root.config;
      this.t = root.t;
      this.track = root.track;
      this.formatMoney = root.formatMoney;
      this.formatDate = root.formatDate;
      this.formatNumber = root.formatNumber;
      this.timers = [];
    }
    $(selector) { return this.root.querySelector(selector); }
    $$(selector) { return Array.from(this.root.querySelectorAll(selector)); }
    on(el, evt, fn, opts) { return this.root.on(el, evt, fn, opts); }
    setTimeout(fn, ms) {
      var id = window.setTimeout(fn, ms);
      this.timers.push({ type: 't', id: id });
      return id;
    }
    setInterval(fn, ms) {
      var id = window.setInterval(fn, ms);
      this.timers.push({ type: 'i', id: id });
      return id;
    }
    clearTimers() {
      this.timers.forEach(function (t) { t.type === 'i' ? clearInterval(t.id) : clearTimeout(t.id); });
      this.timers = [];
    }
    init() {}
    destroy() { this.clearTimers(); }
  }

  /* ------------------------------------------------------------------ */
  /* Toast (aria-live feedback)                                          */
  /* ------------------------------------------------------------------ */

  class SsPdpToast extends SsPdpModule {
    init() {
      this.el = this.$('.ss-pdp__toast');
      if (!this.el) {
        this.el = document.createElement('div');
        this.el.className = 'ss-pdp__toast';
        this.root.appendChild(this.el);
      }
      this.el.setAttribute('role', 'status');
      this.el.setAttribute('aria-live', 'polite');
      hide(this.el);
    }
    show(message, type, duration) {
      if (!this.el || !message) return;
      this.el.textContent = message;
      this.el.setAttribute("data-type", type || "info");
      this.el.classList.remove("ss-pdp__toast--error", "ss-pdp__toast--success");
      if (type === "error" || type === "success") this.el.classList.add("ss-pdp__toast--" + type);
      this.el.setAttribute('role', type === 'error' ? 'alert' : 'status');
      show(this.el);
      this.el.classList.add('is-visible');
      clearTimeout(this._hideTimer);
      this._hideTimer = this.setTimeout(() => {
        this.el.classList.remove('is-visible');
        hide(this.el);
      }, duration || (type === 'error' ? 5000 : 2500));
    }
  }

  /* ------------------------------------------------------------------ */
  /* 1. Gallery                                                          */
  /* ------------------------------------------------------------------ */

  class SsPdpGallery extends SsPdpModule {
    init() {
      this.slider = this.$('.mattress-slider');
      if (!this.slider) return;
      this.slides = Array.from(this.slider.querySelectorAll('.mattress-slide'));
      if (!this.slides.length) return;
      this.dots = this.$$('.mattress-nav-dot');
      this.thumbs = this.$$('.mattress-thumbnail-wrapper');
      this.thumbSlider = this.$('.mattress-thumbnails-slider');
      this.prevBtn = this.$('#mattress-prevBtn');
      this.nextBtn = this.$('#mattress-nextBtn');
      this.status = this.$('.ss-pdp__gallery-status');
      this.skeleton = this.$('.ss-pdp__skeleton');
      this.index = 0;
      this.thumbClicks = 0;
      this.reducedMotion = prefersReducedMotion();

      if (!this.status) {
        this.status = document.createElement('div');
        this.status.className = 'ss-pdp__gallery-status ss-pdp__sr-only';
        this.slider.parentNode.insertBefore(this.status, this.slider.nextSibling);
      }
      this.status.setAttribute('aria-live', 'polite');
      this.status.setAttribute('aria-atomic', 'true');

      this.slider.setAttribute('role', 'region');
      this.slider.setAttribute('aria-roledescription', 'carousel');
      if (!this.slider.hasAttribute('tabindex')) this.slider.setAttribute('tabindex', '0');
      this.slides.forEach((slide, i) => {
        slide.setAttribute('role', 'group');
        slide.setAttribute('aria-roledescription', 'slide');
        slide.setAttribute('aria-label', this.t('galleryStatus', { index: i + 1, total: this.slides.length }));
      });
      var dotList = this.$(".mattress-slider-nav");
      if (dotList) dotList.setAttribute("role", "tablist");
      if (this.thumbSlider) this.thumbSlider.setAttribute("role", "tablist");
      this.dots.forEach((dot, i) => {
        dot.setAttribute("role", "tab");
        if (dot.tagName !== "BUTTON") dot.setAttribute("tabindex", "0"); else dot.setAttribute("type", "button");
        dot.setAttribute('aria-label', this.t('galleryGoTo', { index: i + 1 }));
        this.on(dot, 'click', () => this.goTo(i));
        this.on(dot, 'keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.goTo(i); } });
      });
      this.thumbs.forEach((thumb) => {
        var i = toNumber(thumb.dataset.index, this.thumbs.indexOf(thumb));
        thumb.setAttribute("role", "tab");
        if (thumb.tagName !== "BUTTON") thumb.setAttribute("tabindex", "0"); else thumb.setAttribute("type", "button");
        thumb.setAttribute('aria-label', this.t('galleryGoTo', { index: i + 1 }));
        this.on(thumb, 'click', () => {
          this.thumbClicks++;
          if (this.thumbClicks > 3) this.track('pdp_mul_image', { legacy: [] });
          this.goTo(i);
        });
        this.on(thumb, 'keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.goTo(i); } });
      });

      if (this.prevBtn) {
        this.prevBtn.setAttribute('aria-label', this.t('galleryPrev'));
        this.on(this.prevBtn, 'click', (e) => { e.preventDefault(); this.goTo(this.index - 1); });
      }
      if (this.nextBtn) {
        this.nextBtn.setAttribute('aria-label', this.t('galleryNext'));
        this.on(this.nextBtn, 'click', (e) => { e.preventDefault(); this.goTo(this.index + 1); });
      }

      this.on(this.slider, 'scroll', debounce(() => this.syncFromScroll(), 60), { passive: true });
      this.on(this.slider, 'keydown', (e) => {
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.goTo(this.index - 1); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); this.goTo(this.index + 1); }
        else if (e.key === 'Home') { e.preventDefault(); this.goTo(0); }
        else if (e.key === 'End') { e.preventDefault(); this.goTo(this.slides.length - 1); }
      });

      // Touch swipe (for browsers without scroll-snap support on the slider).
      var touchStartX = null;
      this.on(this.slider, 'touchstart', (e) => { touchStartX = e.touches && e.touches[0] ? e.touches[0].clientX : null; }, { passive: true });
      this.on(this.slider, 'touchend', (e) => {
        if (touchStartX === null || !e.changedTouches || !e.changedTouches[0]) return;
        var dx = e.changedTouches[0].clientX - touchStartX;
        touchStartX = null;
        var snaps = window.getComputedStyle(this.slider).scrollSnapType;
        if (snaps && snaps !== 'none') return; // native snapping handles it
        if (Math.abs(dx) > 40) this.goTo(this.index + (dx < 0 ? 1 : -1));
      }, { passive: true });

      // Lazy-load media when slides come into view.
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            var slide = entry.target;
            if (entry.isIntersecting) {
              this.loadSlideMedia(slide);
              if (entry.intersectionRatio >= 0.6) {
                var i = this.slides.indexOf(slide);
                if (i !== -1 && i !== this.index) { this.index = i; this.render(); }
                this.playSlideVideo(slide);
              }
            } else {
              this.pauseSlideVideo(slide);
            }
          });
        }, { root: this.slider, threshold: [0, 0.6] });
        this.slides.forEach((s) => this.observer.observe(s));
      } else {
        this.slides.forEach((s) => this.loadSlideMedia(s));
      }

      this.$$(".mattress-track").forEach((track) => {
        if (this.reducedMotion) track.classList.add("is-paused");
        ["touchstart", "focusin", "mouseenter"].forEach((evt) => this.on(track, evt, () => track.classList.add("is-paused"), { passive: true }));
        ["touchend", "focusout", "mouseleave"].forEach((evt) => this.on(track, evt, () => { if (!this.reducedMotion) track.classList.remove("is-paused"); }, { passive: true }));
      });

      this.loadSlideMedia(this.slides[0]);
      if (this.slides[1]) this.loadSlideMedia(this.slides[1]);
      if (this.skeleton) hide(this.skeleton);
      this.render(true);

      this.on(this.root, 'ss-pdp:variant', (e) => {
        var variant = e.detail && e.detail.variant;
        var mediaId = variant && (get(variant, 'featured_media.id', null) || get(variant, 'featured_image.id', null));
        if (!mediaId) return;
        var target = this.slides.findIndex((s) => String(s.dataset.mediaId || '') === String(mediaId));
        if (target !== -1) this.goTo(target);
      });
    }

    loadSlideMedia(slide) {
      if (!slide || slide.dataset.ssLoaded === '1') return;
      slide.dataset.ssLoaded = '1';
      slide.querySelectorAll('img[data-src], img[data-srcset]').forEach((img) => {
        if (img.dataset.srcset) { img.setAttribute('srcset', img.dataset.srcset); img.removeAttribute('data-srcset'); }
        if (img.dataset.src) { img.setAttribute('src', img.dataset.src); img.removeAttribute('data-src'); }
        img.classList.remove('mattress-prod-lazy-img');
      });
      slide.querySelectorAll('video.lazy-video').forEach((video) => {
        var changed = false;
        video.querySelectorAll('source[data-src]').forEach((source) => {
          source.setAttribute('src', source.dataset.src);
          source.removeAttribute('data-src');
          changed = true;
        });
        if (changed) {
          try { video.load(); } catch (e) { /* noop */ }
        }
        video.classList.remove('lazy-video');
        video.classList.add('ss-pdp-video-ready');
        if (this.reducedMotion) {
          video.removeAttribute('autoplay');
          video.setAttribute('controls', '');
        }
      });
    }

    playSlideVideo(slide) {
      this.slides.forEach((s) => { if (s !== slide) this.pauseSlideVideo(s); });
      if (this.reducedMotion) return;
      slide.querySelectorAll('video').forEach((video) => {
        if (video.offsetParent === null) return;
        var p = video.play();
        if (p && typeof p.catch === 'function') p.catch(function () { /* autoplay blocked */ });
      });
    }

    pauseSlideVideo(slide) {
      slide.querySelectorAll('video').forEach((video) => { try { if (!video.paused) video.pause(); } catch (e) { /* noop */ } });
    }

    goTo(index, silent) {
      var total = this.slides.length;
      if (!total) return;
      var i = ((index % total) + total) % total;
      this.index = i;
      var slide = this.slides[i];
      this.loadSlideMedia(slide);
      if (this.slides[i + 1]) this.loadSlideMedia(this.slides[i + 1]);
      this.slider.scrollTo({ left: slide.offsetLeft - this.slider.offsetLeft, behavior: this.reducedMotion ? 'auto' : 'smooth' });
      this.render(silent);
      this.playSlideVideo(slide);
    }

    syncFromScroll() {
      var width = this.slides[0] ? this.slides[0].offsetWidth : this.slider.clientWidth;
      if (!width) return;
      var i = Math.round(this.slider.scrollLeft / width);
      i = Math.max(0, Math.min(this.slides.length - 1, i));
      if (i !== this.index) {
        this.index = i;
        this.loadSlideMedia(this.slides[i]);
        this.render();
        this.playSlideVideo(this.slides[i]);
      }
    }

    render(silent) {
      var i = this.index;
      this.dots.forEach((dot, d) => {
        dot.classList.toggle('mattress-active', d === i);
        dot.setAttribute("aria-selected", d === i ? "true" : "false");
        dot.setAttribute("tabindex", d === i ? "0" : "-1");
      });
      this.thumbs.forEach((thumb) => {
        var ti = toNumber(thumb.dataset.index, -1);
        var active = ti === i;
        thumb.classList.toggle('active', active);
        thumb.setAttribute("aria-selected", active ? "true" : "false");
        thumb.setAttribute("tabindex", active ? "0" : "-1");
      });
      this.slides.forEach((slide, s) => { slide.setAttribute('aria-hidden', s === i ? 'false' : 'true'); });
      this.scrollActiveThumbIntoView();
      if (this.status && !silent) {
        this.status.textContent = this.t('galleryStatus', { index: i + 1, total: this.slides.length });
      }
    }

    scrollActiveThumbIntoView() {
      var sliderThumb = this.thumbSlider;
      if (!sliderThumb) return;
      var active = sliderThumb.querySelector('.mattress-thumbnail-wrapper.active');
      if (!active) return;
      var containerWidth = sliderThumb.offsetWidth;
      var itemWidth = active.offsetWidth + 10;
      var itemCenter = active.offsetLeft + itemWidth / 2;
      var position = itemCenter - containerWidth / 2;
      position = Math.max(0, Math.min(position, sliderThumb.scrollWidth - containerWidth));
      sliderThumb.scrollTo({ left: position, behavior: this.reducedMotion ? 'auto' : 'smooth' });
    }

    destroy() {
      super.destroy();
      if (this.observer) this.observer.disconnect();
      if (this.slides) this.slides.forEach((s) => this.pauseSlideVideo(s));
    }
  }

  /* ------------------------------------------------------------------ */
  /* 2. Share                                                            */
  /* ------------------------------------------------------------------ */

  class SsPdpShare extends SsPdpModule {
    init() {
      if (get(this.config, 'features.share', true) === false) return;
      this.toggler = this.$('.c-share__toggler');
      this.options = this.$('.c-share_options');
      this.mobileBtn = this.$('.mobile-btn-share');
      if (!this.toggler) return;

      this.toggler.setAttribute('role', 'button');
      this.toggler.setAttribute('tabindex', '0');
      this.toggler.setAttribute('aria-label', this.t('shareLabel'));
      this.toggler.setAttribute('aria-expanded', 'false');

      var handler = (e) => {
        this.track('share', { legacy: ['PDP'], location: 'pdp' });
        var shareData = {
          title: get(this.config, 'product.title', document.title),
          url: get(this.config, 'product.absoluteUrl', '') || window.location.href
        };
        if (navigator.share && isSmallScreen()) {
          e.preventDefault();
          navigator.share(shareData).catch(() => { /* user cancelled */ });
          return;
        }
        if (this.options) {
          var active = this.options.classList.toggle('share-active');
          if (this.mobileBtn) this.mobileBtn.classList.toggle('share-active', active);
          this.toggler.setAttribute('aria-expanded', active ? 'true' : 'false');
        } else {
          e.preventDefault();
          this.copyLink(shareData.url);
        }
      };
      this.on(this.toggler, 'click', handler);
      this.on(this.toggler, 'keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handler(e); } });

      this.$$('[data-ss-copy-link]').forEach((btn) => {
        this.on(btn, 'click', (e) => {
          e.preventDefault();
          this.copyLink(get(this.config, 'product.absoluteUrl', '') || window.location.href);
        });
      });
    }

    copyLink(url) {
      var done = () => this.root.toast.show(this.t('copyLinkSuccess'), 'success');
      var fail = () => this.root.toast.show(this.t('copyLinkError'), 'error');
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(done, fail);
        return;
      }
      try {
        var input = document.createElement('input');
        input.value = url;
        input.setAttribute('readonly', '');
        input.className = 'ss-pdp__sr-only';
        this.root.appendChild(input);
        input.select();
        var ok = document.execCommand('copy');
        this.root.removeChild(input);
        ok ? done() : fail();
      } catch (e) { fail(); }
    }
  }

  /* ------------------------------------------------------------------ */
  /* 3. Wishlist                                                         */
  /* ------------------------------------------------------------------ */

  class SsPdpWishlist extends SsPdpModule {
    init() {
      if (get(this.config, 'features.wishlist', true) === false) return;
      this.button = this.$('#pdpwishlist');
      if (!this.button) return;
      this.productId = String(get(this.config, 'product.id', this.button.dataset.prodId || ''));
      this.getUrl = get(this.config, 'endpoints.wishlistGet', '');
      this.postUrl = get(this.config, 'endpoints.wishlistPost', '');
      this.apiKey = get(this.config, 'apiKeys.wishlist', '');
      this.customerId = get(this.config, 'customer.id', null);
      this.loggedIn = !!get(this.config, 'customer.loggedIn', false) && this.customerId;
      this.useApi = !!(this.getUrl && this.postUrl && this.loggedIn);
      this.busy = false;

      this.button.setAttribute('role', 'button');
      this.button.setAttribute('tabindex', '0');
      this.setFilled(this.button.classList.contains('remove_from_wishlist'));

      this.on(this.button, 'click', (e) => { e.preventDefault(); this.toggle(); });
      this.on(this.button, 'keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.toggle(); } });

      this.restoreState();
    }

    setFilled(filled) {
      this.filled = !!filled;
      this.button.classList.toggle('add_to_wishlist', !this.filled);
      this.button.classList.toggle('remove_from_wishlist', this.filled);
      this.button.classList.toggle("is-wishlisted", this.filled);
      this.button.setAttribute('aria-pressed', this.filled ? 'true' : 'false');
      this.button.setAttribute('aria-label', this.t(this.filled ? 'wishlistRemove' : 'wishlistAdd'));
    }

    restoreState() {
      var synced = getCookie(WISHLIST_SYNC_COOKIE);
      if (synced && String(get(synced, 'productId', '')) === this.productId) {
        this.setFilled(true);
        deleteCookie(WISHLIST_SYNC_COOKIE);
      }
      if (this.useApi) {
        var url = this.getUrl.indexOf('{customerId}') !== -1
          ? this.getUrl.replace('{customerId}', encodeURIComponent(String(this.customerId)))
          : this.getUrl.replace(/\/?$/, '/') + encodeURIComponent(String(this.customerId));
        request(url, { method: 'GET', headers: this.headers() })
          .then((res) => {
            var list = get(res, 'data.wishlist', []);
            if (Array.isArray(list) && list.some((p) => String(p.productId) === this.productId)) this.setFilled(true);
          })
          .catch((e) => warn('wishlist status', e));
      } else {
        var local = storage.get(WISHLIST_LOCAL_KEY);
        if (Array.isArray(local) && local.indexOf(this.productId) !== -1) this.setFilled(true);
      }
    }

    headers() {
      var h = { 'Content-Type': 'application/json' };
      if (this.apiKey) h['x-api-key'] = this.apiKey;
      return h;
    }

    toggle() {
      if (this.busy) return;
      this.track('wishlist_button', { legacy: ['pdp_img'], product_id: this.productId });
      if (!this.useApi) {
        var local = storage.get(WISHLIST_LOCAL_KEY);
        if (!Array.isArray(local)) local = [];
        if (this.filled) {
          local = local.filter((id) => id !== this.productId);
          this.setFilled(false);
          this.root.toast.show(this.t('wishlistRemoved'), 'info');
        } else {
          local.push(this.productId);
          this.setFilled(true);
          this.root.toast.show(this.t('wishlistAdded'), 'success');
        }
        storage.set(WISHLIST_LOCAL_KEY, local);
        if (this.getUrl && this.postUrl && !this.loggedIn) storage.set(WISHLIST_PENDING_KEY, this.productId);
        return;
      }
      var variant = this.root.currentVariant || {};
      var body = this.filled
        ? {
          userId: String(this.customerId),
          productId: this.productId,
          productUrl: get(this.config, 'product.handle', ''),
          action: 'REMOVE_FROM_WISHLIST'
        }
        : {
          userId: String(this.customerId),
          productId: this.productId,
          productUrl: get(this.config, 'product.handle', ''),
          productType: get(this.config, 'product.type', ''),
          productAvailability: String(!!variant.available),
          productPrice: String(toNumber(variant.price, 0)),
          variantId: String(variant.id || ''),
          action: 'ADD_TO_WISHLIST'
        };
      this.busy = true;
      this.button.setAttribute('aria-busy', 'true');
      request(this.postUrl, { method: 'POST', headers: this.headers(), body: JSON.stringify(body) })
        .then((res) => {
          if (get(res, 'data.success', false) === true) {
            this.setFilled(!this.filled);
            this.root.toast.show(this.t(this.filled ? 'wishlistAdded' : 'wishlistRemoved'), 'success');
          } else {
            this.root.toast.show(this.t('wishlistError'), 'error');
          }
        })
        .catch((e) => { warn('wishlist', e); this.root.toast.show(this.t('wishlistError'), 'error'); })
        .finally(() => { this.busy = false; this.button.removeAttribute('aria-busy'); });
    }
  }

  /* ------------------------------------------------------------------ */
  /* 4. Review-count scroll                                              */
  /* ------------------------------------------------------------------ */

  class SsPdpScrollTo extends SsPdpModule {
    init() {
      this.$$('[data-scroll-to]').forEach((el) => {
        el.setAttribute('role', 'link');
        if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '0');
        var go = (e) => {
          var key = (el.dataset.scrollTo || '').trim();
          if (!key) return;
          var target = document.getElementById(key) || document.querySelector('.' + key.replace(/[^\w-]/g, ''));
          if (!target) return;
          e.preventDefault();
          target.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
          this.track('review_scroll', { target: key });
        };
        this.on(el, 'click', go);
        this.on(el, 'keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') go(e); });
      });
    }
  }

  /* ------------------------------------------------------------------ */
  /* 5. Price                                                            */
  /* ------------------------------------------------------------------ */

  class SsPdpPrice extends SsPdpModule {
    init() {
      this.on(this.root, 'ss-pdp:variant', (e) => this.update(e.detail && e.detail.variant));
      this.on(this.root, "ss-pdp:variant-preview", (e) => this.update(e.detail && e.detail.variant, e.detail && e.detail.scope));
      if (this.root.currentVariant) this.update(this.root.currentVariant);
    }

    update(variant, scope) {
      if (!variant) return;
      var root = scope || this.root;
      var q = (sel) => Array.from(root.querySelectorAll(sel));
      var q1 = (sel) => root.querySelector(sel);
      var price = toNumber(variant.price, 0);
      var compare = toNumber(variant.compare_at_price, 0);
      var hasCompare = compare > price;
      var percent = hasCompare ? Math.round(((compare - price) / compare) * 100) : 0;

      q('.dev_update-price-onvariantchange').forEach((el) => {
        el.textContent = this.formatMoney(price);
        if (el.hasAttribute('data-price')) el.setAttribute('data-price', String(price));
        if (el.hasAttribute('data-variant-id')) el.setAttribute('data-variant-id', String(variant.id || ''));
      });
      q('[data-price]:not(.dev_update-price-onvariantchange)').forEach((el) => el.setAttribute('data-price', String(price)));
      q('[data-variant-id]:not(.dev_update-price-onvariantchange)').forEach((el) => {
        if (!el.closest('.mattress-slide')) el.setAttribute('data-variant-id', String(variant.id || ''));
      });
      q('.dev_update-compareatprice-onvariantchange').forEach((el) => {
        var span = el.querySelector('span') || el;
        span.textContent = hasCompare ? this.formatMoney(compare) : '';
        show(el, hasCompare);
      });
      q('.dev_update-discount-onvariantchange').forEach((el) => {
        el.textContent = percent > 0 ? this.t('discountPercent', { percent: percent }) : '';
        show(el, percent > 0);
      });
      q('.ss-pdp__badge-sale').forEach((el) => show(el, percent > 0));

      // Reserve / "pay now, rest later" strip
      q('[data-pdp-inline-emi-strip]').forEach((strip) => {
        var amountEl = strip.querySelector('.pdp-inline-bnpl-price');
        if (!amountEl) return;
        var fixed = toNumber(strip.dataset.reserveAmount, toNumber(get(this.config, 'reserve.amount', 0), 0));
        var pct = toNumber(strip.dataset.reservePercent, toNumber(get(this.config, 'reserve.percent', 0), 0));
        var reserve = fixed > 0 ? fixed : (pct > 0 ? Math.round(price * pct / 100) : 0);
        amountEl.textContent = reserve > 0 ? this.formatMoney(reserve) : '';
        show(strip, reserve > 0);
        strip.setAttribute('data-default-variant-id', String(variant.id || ''));
      });

      // Offers popup header + price breakdown
      q('.pdp-dc-header-p-varient').forEach((el) => { el.textContent = variant.public_title || variant.title || ''; });
      var mainValue = q1('.pdp-dc-price-main .pdp-dc-price-value');
      var discountedValue = q1('.pdp-dc-price-discounted .pdp-dc-price-value');
      var badge = q1('.pdp-dc-price-badge span') || q1('.pdp-dc-price-badge');
      if (mainValue) mainValue.textContent = this.formatMoney(hasCompare ? compare : price);
      if (discountedValue) discountedValue.textContent = this.formatMoney(price);
      if (badge) { badge.textContent = percent > 0 ? this.t('discountPercent', { percent: percent }) : ''; show(badge.closest('.pdp-dc-price-badge') || badge, percent > 0); }
      var discounted = q1('.pdp-dc-price-discounted');
      if (discounted) show(discounted, hasCompare);
      var notice = q1('.pdp-dc-price-discounted-notice');
      if (notice) show(notice, hasCompare);
    }
  }

  /* ------------------------------------------------------------------ */
  /* 6. Variant picker (bottom sheet / modal) incl. custom size + units  */
  /* ------------------------------------------------------------------ */

  class SsPdpVariantPicker extends SsPdpModule {
    init() {
      this.variants = Array.isArray(this.root.variants) ? this.root.variants : [];
      this.customVariants = Array.isArray(this.root.customVariants) ? this.root.customVariants : [];
      this.catPos = toNumber(get(this.config, 'options.categoryPosition', 0), 0);
      this.heightPos = toNumber(get(this.config, 'options.heightPosition', 0), 0);
      this.sizePos = toNumber(get(this.config, 'options.sizePosition', 0), 0);
      this.sizeUnit = String(get(this.config, 'options.sizeUnit', 'cm')).toLowerCase();
      this.displayUnit = String(get(this.config, 'options.defaultDisplayUnit', this.sizeUnit)).toLowerCase();
      // A dedicated custom-size product is optional: without one the regular
      // variants carry the dimensions as line item properties (see customPool()).
      this.customEnabled = !!get(this.config, 'customSize.enabled', false);
      this.customMin = toNumber(get(this.config, 'customSize.min', 0), 0);
      this.customMax = toNumber(get(this.config, 'customSize.max', 0), 0);
      this.customUnit = String(get(this.config, 'customSize.unit', this.sizeUnit)).toLowerCase();

      this.wrapper = this.$('#pv-pdp-variant-full-wrapper');
      this.sheet = this.$('#dev_pv_pdp_variant_wrapper') || this.wrapper;
      this.closeBtn = this.$('#dev_pv-variant-popup-close-btn');
      this.openers = this.$$('.dev_click-product_size, .size-box');
      this.sizeSection = this.$('.pv_section_size');
      this.customWrapper = this.$('.pv-option-section-custom-size-wrapper');
      this.lengthInput = this.$('#lengthInput');
      this.breadthInput = this.$('#breadthInput');
      this.errorWrap = this.$('.dimension-error-wrap');
      this.errorText = this.$('#dimension-error');
      this.successBox = this.$('#validation-success');
      this.outOfStock = this.wrapper ? this.wrapper.querySelector('.out_of_stock') : null;
      this.isOpen = false;

      this.renderSelected(this.root.currentVariant, this.root.currentCustom);
      this.on(this.root, 'ss-pdp:variant', (e) => {
        this.renderSelected(e.detail && e.detail.variant, e.detail && e.detail.custom);
        if (!this.isOpen && e.detail && e.detail.source !== 'picker') this.syncRadiosToVariant(e.detail.variant, e.detail.custom);
      });

      if (!this.wrapper) return;

      this.categoryRadios = Array.from(this.wrapper.querySelectorAll('input[name="option-category"]'));
      this.heightRadios = Array.from(this.wrapper.querySelectorAll('input[name="option-height"]'));
      this.sizeRadios = Array.from(this.wrapper.querySelectorAll('input[name="option-size"]'));
      this.unitRadios = Array.from(this.wrapper.querySelectorAll('.size-unit-selector'));
      this.customRadio = this.categoryRadios.find((r) => r.id === 'custom_variant' || r.hasAttribute('data-ss-custom') || String(r.value).toLowerCase() === 'custom') || null;
      if (this.customRadio && !this.customEnabled) {
        var item = this.customRadio.closest('.pv_option_item') || this.customRadio;
        hide(item);
        this.customRadio.disabled = true;
      }

      this.wrapper.setAttribute('role', 'dialog');
      this.wrapper.setAttribute('aria-modal', 'true');
      var heading = this.wrapper.querySelector('.pv-heading');
      if (heading) {
        if (!heading.id) heading.id = 'ss-pdp-variant-heading-' + (this.config.sectionId || 'x');
        this.wrapper.setAttribute('aria-labelledby', heading.id);
      }
      hide(this.wrapper);
      this.wrapper.setAttribute('aria-hidden', 'true');

      this.openers.forEach((opener) => {
        opener.setAttribute('role', 'button');
        if (!opener.hasAttribute('tabindex')) opener.setAttribute('tabindex', '0');
        opener.setAttribute('aria-haspopup', 'dialog');
        opener.setAttribute('aria-expanded', 'false');
        this.on(opener, 'click', (e) => { e.preventDefault(); this.open(opener); });
        this.on(opener, 'keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.open(opener); } });
      });
      if (this.closeBtn) {
        this.closeBtn.setAttribute('role', 'button');
        this.closeBtn.setAttribute('tabindex', '0');
        this.closeBtn.setAttribute('aria-label', this.t('close'));
        this.on(this.closeBtn, 'click', (e) => { e.preventDefault(); this.close(); });
        this.on(this.closeBtn, 'keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.close(); } });
      }
      this.on(this.wrapper, 'click', (e) => { if (e.target === this.wrapper) this.close(); });

      this.categoryRadios.forEach((r) => this.on(r, 'change', () => this.onOptionChange('category')));
      this.heightRadios.forEach((r) => this.on(r, 'change', () => this.onOptionChange('height')));
      this.sizeRadios.forEach((r) => this.on(r, 'change', () => this.onOptionChange('size')));
      this.unitRadios.forEach((r) => {
        if (String(r.value).toLowerCase() === this.displayUnit) r.checked = true;
        this.on(r, 'change', () => {
          if (r.checked) {
            this.displayUnit = String(r.value).toLowerCase();
            this.relabelSizes();
            this.renderSelected(this.root.currentVariant, this.root.currentCustom);
            this.track('size_unit', { unit: this.displayUnit });
          }
        });
      });
      [this.lengthInput, this.breadthInput].forEach((input) => {
        if (!input) return;
        input.setAttribute('inputmode', 'decimal');
        this.on(input, 'input', () => this.validateCustom(true));
      });

      // Confirm button inside the sheet applies the selection.
      this.wrapper.querySelectorAll('.pv-variant-atc-wrapper .mattress-pdp-atc-btn').forEach((btn) => {
        btn.setAttribute('data-ss-variant-confirm', '');
        this.on(btn, 'click', (e) => { e.preventDefault(); e.stopPropagation(); this.confirm(); });
      });

      // "Learn how to measure" accordion with lazy video.
      var learnToggle = this.$('#dev_pv-variant-learn-text-icon');
      var learnBody = this.$('#dev_pv-variant-learn-video-prnt');
      if (learnToggle && learnBody) {
        learnToggle.setAttribute('role', 'button');
        learnToggle.setAttribute('tabindex', '0');
        learnToggle.setAttribute('aria-expanded', 'false');
        if (!learnBody.id) learnBody.id = 'ss-pdp-learn-body';
        learnToggle.setAttribute('aria-controls', learnBody.id);
        var learnWrap = learnToggle.closest(".pv-variant-learn-how-to") || learnToggle.parentElement;
        learnBody.setAttribute("aria-hidden", "true");
        var toggleLearn = () => {
          var open = !learnWrap.classList.contains("is-open");
          learnWrap.classList.toggle("is-open", open);
          learnToggle.classList.toggle("is-open", open);
          learnToggle.setAttribute("aria-expanded", open ? "true" : "false");
          learnBody.setAttribute("aria-hidden", open ? "false" : "true");
          if (open) {
            learnBody.querySelectorAll('video').forEach((video) => {
              video.querySelectorAll('source[data-src]').forEach((s) => { s.src = s.dataset.src; s.removeAttribute('data-src'); });
              if (video.dataset.src) { video.src = video.dataset.src; video.removeAttribute('data-src'); }
              try { video.load(); } catch (err) { /* noop */ }
            });
            learnBody.querySelectorAll('iframe[data-src]').forEach((f) => { f.src = f.dataset.src; f.removeAttribute('data-src'); });
          } else {
            learnBody.querySelectorAll('video').forEach((v) => { try { v.pause(); } catch (err) { /* noop */ } });
          }
        };
        this.on(learnToggle, 'click', toggleLearn);
        this.on(learnToggle, 'keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleLearn(); } });
      }

      // Port of the "Tab cycles categories" behaviour (desktop only, scoped to the category group).
      this.on(this.wrapper, 'keydown', (e) => {
        if (e.key !== 'Tab' || isSmallScreen() || !this.isOpen) return;
        var group = this.wrapper.querySelector('.pv-option-section-category');
        if (!group || !group.contains(document.activeElement)) return;
        var radios = this.categoryRadios.filter((r) => !r.disabled && isVisible(r.closest('.pv_option_item') || r));
        if (!radios.length) return;
        var current = radios.indexOf(document.activeElement);
        if (current === -1) current = radios.findIndex((r) => r.checked);
        var next;
        if (e.shiftKey) {
          if (current <= 0) return; // leave the group backwards
          next = current - 1;
        } else {
          if (current === radios.length - 1) return; // leave the group forwards
          next = current === -1 ? 0 : current + 1;
        }
        e.preventDefault();
        e.stopPropagation();
        radios[next].focus();
        if (!radios[next].checked) {
          radios[next].checked = true;
          radios[next].dispatchEvent(new Event('change', { bubbles: true }));
        }
      });

      this.relabelSizes();
      this.syncRadiosToVariant(this.root.currentVariant, this.root.currentCustom);
      this.refreshAvailability();
    }

    /* ---- option helpers ---- */

    optionAt(variant, pos) {
      if (!variant || !pos) return null;
      if (Array.isArray(variant.options) && variant.options.length >= pos) return variant.options[pos - 1];
      return variant['option' + pos] === undefined ? null : variant['option' + pos];
    }

    matches(variant, cat, height, size) {
      if (this.catPos && cat !== null && cat !== undefined && String(this.optionAt(variant, this.catPos)) !== String(cat)) return false;
      if (this.heightPos && height !== null && height !== undefined && String(this.optionAt(variant, this.heightPos)) !== String(height)) return false;
      if (this.sizePos && size !== null && size !== undefined && String(this.optionAt(variant, this.sizePos)) !== String(size)) return false;
      return true;
    }

    findVariant(cat, height, size) {
      return this.variants.find((v) => this.matches(v, cat, height, size)) || null;
    }

    /* Variants that can carry a custom size: the dedicated custom product when
       configured, otherwise the regular variants (dimensions travel as line
       item properties, like the legacy storefront did). */
    customPool() {
      return this.customVariants.length ? this.customVariants : this.variants;
    }

    findCustomVariant(height) {
      var pool = this.customPool();
      var list = pool.filter((v) => !this.heightPos || height === null || height === undefined || String(this.optionAt(v, this.heightPos)) === String(height));
      if (!this.customVariants.length) {
        var cat = this.checkedValue(this.categoryRadios.filter((r) => r !== this.customRadio));
        var current = this.root.currentVariant;
        var sameCat = list.filter((v) => cat !== null && String(this.optionAt(v, this.catPos)) === String(cat));
        var sameAsCurrent = current ? list.filter((v) => String(this.optionAt(v, this.catPos)) === String(this.optionAt(current, this.catPos))) : [];
        list = (sameAsCurrent.length ? sameAsCurrent : (sameCat.length ? sameCat : list));
      }
      return list.find((v) => v.available) || list[0] || null;
    }

    checkedValue(radios) {
      var r = (radios || []).find((x) => x.checked);
      return r ? r.value : null;
    }

    labelFor(radio) {
      if (!radio || !radio.id) return null;
      return this.wrapper.querySelector('label[for="' + radio.id.replace(/"/g, '') + '"]');
    }

    isCustomSelected() {
      return !!(this.customRadio && this.customRadio.checked && this.customEnabled);
    }

    /* ---- size formatting ---- */

    unitLabel(unit) {
      var u = String(unit || '').toLowerCase();
      if (u === 'cm') return this.t('unitCm');
      if (u === 'ft') return this.t('unitFt');
      return this.t('unitIn');
    }

    formatSize(length, width, fromUnit, toUnit) {
      var from = fromUnit || this.sizeUnit;
      var to = toUnit || this.displayUnit;
      var l = convertLength(length, from, to);
      var w = convertLength(width, from, to);
      if (!Number.isFinite(l) || !Number.isFinite(w)) return '';
      return this.t('sizeLabel', { length: this.formatNumber(l, 1), width: this.formatNumber(w, 1), unit: this.unitLabel(to) });
    }

    formatSizeValue(value) {
      var parsed = parseSizeValue(value);
      if (!parsed) return String(value || '');
      return this.formatSize(parsed.length, parsed.width, this.sizeUnit, this.displayUnit);
    }

    relabelSizes() {
      if (!this.sizeRadios) return;
      this.sizeRadios.forEach((radio) => {
        var label = this.labelFor(radio);
        if (!label) return;
        var parsed = parseSizeValue(radio.value) || (radio.dataset.length && radio.dataset.breadth ? { length: parseFloat(radio.dataset.length), width: parseFloat(radio.dataset.breadth) } : null);
        if (!parsed) return;
        label.textContent = this.formatSize(parsed.length, parsed.width, this.sizeUnit, this.displayUnit);
      });
    }

    /* ---- state ---- */

    syncRadiosToVariant(variant, custom) {
      if (!this.wrapper || !variant) return;
      var isCustom = !!custom && this.customRadio && this.customEnabled;
      if (isCustom) {
        this.customRadio.checked = true;
        if (this.lengthInput && custom.length !== undefined) this.lengthInput.value = custom.length;
        if (this.breadthInput && custom.width !== undefined) this.breadthInput.value = custom.width;
      } else {
        var cat = this.optionAt(variant, this.catPos);
        this.categoryRadios.forEach((r) => { if (r !== this.customRadio) r.checked = String(r.value) === String(cat); });
      }
      var height = this.optionAt(variant, this.heightPos);
      this.heightRadios.forEach((r) => { r.checked = String(r.value) === String(height); });
      var size = this.optionAt(variant, this.sizePos);
      this.sizeRadios.forEach((r) => { r.checked = String(r.value) === String(size); });
      this.refreshAvailability();
    }

    refreshAvailability() {
      if (!this.wrapper) return;
      var custom = this.isCustomSelected();
      var cat = custom ? null : this.checkedValue(this.categoryRadios);
      var pool = custom ? this.customPool() : this.variants;

      var heightFixed = false;
      this.heightRadios.forEach((radio) => {
        var ok = pool.some((v) => (custom || !this.catPos || String(this.optionAt(v, this.catPos)) === String(cat)) && String(this.optionAt(v, this.heightPos)) === String(radio.value));
        this.setRadioEnabled(radio, ok);
        if (!ok && radio.checked) { radio.checked = false; heightFixed = true; }
      });
      if (heightFixed || !this.heightRadios.some((r) => r.checked)) {
        var firstHeight = this.heightRadios.find((r) => !r.disabled);
        if (firstHeight) firstHeight.checked = true;
      }
      var height = this.checkedValue(this.heightRadios);

      var sizeFixed = false;
      this.sizeRadios.forEach((radio) => {
        var ok = !custom && this.variants.some((v) => this.matches(v, cat, height, radio.value));
        this.setRadioEnabled(radio, ok);
        if (!ok && radio.checked) { radio.checked = false; sizeFixed = true; }
      });
      if (!custom && (sizeFixed || !this.sizeRadios.some((r) => r.checked))) {
        var firstSize = this.sizeRadios.find((r) => !r.disabled);
        if (firstSize) firstSize.checked = true;
      }

      if (this.sizeSection) show(this.sizeSection, !custom);
      if (this.customWrapper) show(this.customWrapper, custom);
      if (custom) {
        var heightValue = parseFloat(String(height || '').replace(',', '.'));
        var heightEl = this.$('#dev_pv-dimension-callout-height-value');
        if (heightEl) heightEl.textContent = Number.isFinite(heightValue) ? this.formatNumber(heightValue, 1) : (height || '');
        this.validateCustom(false);
      } else {
        this.clearCustomFeedback();
      }
      this.previewPending();
    }

    setRadioEnabled(radio, enabled) {
      radio.disabled = !enabled;
      radio.setAttribute('aria-disabled', enabled ? 'false' : 'true');
      var label = this.labelFor(radio);
      if (label) label.classList.toggle('disabled', !enabled);
      var item = radio.closest('.pv_option_item');
      if (item) item.classList.toggle('is-unavailable', !enabled);
    }

    onOptionChange(kind) {
      this.refreshAvailability();
      var pending = this.pendingVariant();
      this.track('variant_option', { option: kind, variant_id: pending ? pending.id : '' });
    }

    pendingVariant() {
      if (!this.wrapper) return this.root.currentVariant;
      var height = this.checkedValue(this.heightRadios);
      if (this.isCustomSelected()) return this.findCustomVariant(height);
      return this.findVariant(this.checkedValue(this.categoryRadios), height, this.checkedValue(this.sizeRadios));
    }

    previewPending() {
      var variant = this.pendingVariant();
      if (this.outOfStock) {
        var soldOut = !!variant && !variant.available;
        this.outOfStock.textContent = this.t('outOfStock');
        show(this.outOfStock, soldOut);
      }
      this.wrapper.querySelectorAll('.pv-variant-atc-wrapper').forEach((wrap) => {
        var atc = wrap.querySelector('.mattress-pdp-atc-btn');
        var notify = wrap.querySelector('.notify-btn');
        var soldOut = !!variant && !variant.available;
        if (atc) show(atc, !soldOut);
        if (notify) show(notify, soldOut);
      });
      if (variant) {
        this.root.dispatchEvent(new CustomEvent('ss-pdp:variant-preview', { detail: { variant: variant, scope: this.wrapper } }));
      }
    }

    /* ---- custom size ---- */

    readCustom() {
      var l = this.lengthInput ? parseFloat(String(this.lengthInput.value).replace(',', '.')) : NaN;
      var w = this.breadthInput ? parseFloat(String(this.breadthInput.value).replace(',', '.')) : NaN;
      return { length: l, width: w };
    }

    validateCustom(fromInput) {
      if (!this.isCustomSelected()) return true;
      var dims = this.readCustom();
      var unit = this.unitLabel(this.customUnit);
      var calloutL = this.$('#dev_pv-dimension-callout-length');
      var calloutB = this.$('#dev_pv-dimension-callout-breadth');
      var valueL = this.$('#dev_pv-dimension-callout-length-value');
      var valueB = this.$('#dev_pv-dimension-callout-breadth-value');
      if (calloutL) show(calloutL, Number.isFinite(dims.length));
      if (calloutB) show(calloutB, Number.isFinite(dims.width));
      if (valueL) valueL.textContent = Number.isFinite(dims.length) ? this.formatNumber(dims.length, 1) : '';
      if (valueB) valueB.textContent = Number.isFinite(dims.width) ? this.formatNumber(dims.width, 1) : '';

      var empty = !Number.isFinite(dims.length) || !Number.isFinite(dims.width);
      if (empty) {
        if (fromInput) this.clearCustomFeedback(); else this.showCustomError(this.t('customSizeRequired'));
        return false;
      }
      var inRange = (v) => (!this.customMin || v >= this.customMin) && (!this.customMax || v <= this.customMax);
      if (!inRange(dims.length) || !inRange(dims.width)) {
        this.showCustomError(this.t('customSizeError', { min: this.formatNumber(this.customMin, 1), max: this.formatNumber(this.customMax, 1), unit: unit }));
        return false;
      }
      if (!this.findCustomVariant(this.checkedValue(this.heightRadios))) {
        this.showCustomError(this.t('customSizeUnavailable'));
        return false;
      }
      this.showCustomSuccess();
      return true;
    }

    showCustomError(message) {
      if (this.errorText) this.errorText.textContent = message;
      if (this.errorWrap) { show(this.errorWrap); this.errorWrap.setAttribute('role', 'alert'); }
      if (this.successBox) hide(this.successBox);
      [this.lengthInput, this.breadthInput].forEach((i) => { if (i) i.setAttribute('aria-invalid', 'true'); });
    }

    showCustomSuccess() {
      if (this.errorWrap) hide(this.errorWrap);
      if (this.successBox) {
        var p = this.successBox.querySelector('p') || this.successBox;
        var days = toNumber(get(this.config, 'customSize.extraDays', 0), 0);
        if (days > 0) p.textContent = this.t('customSizeNote', { days: days });
        show(this.successBox);
      }
      [this.lengthInput, this.breadthInput].forEach((i) => { if (i) i.setAttribute('aria-invalid', 'false'); });
    }

    clearCustomFeedback() {
      if (this.errorWrap) hide(this.errorWrap);
      if (this.successBox) hide(this.successBox);
    }

    /* ---- open / close / confirm ---- */

    open(opener) {
      if (!this.wrapper || this.isOpen) return;
      this.isOpen = true;
      this.syncRadiosToVariant(this.root.currentVariant, this.root.currentCustom);
      show(this.wrapper);
      this.wrapper.setAttribute('aria-hidden', 'false');
      document.body.classList.add("pv-variant-popup-open");
      this.openers.forEach((o) => o.setAttribute('aria-expanded', 'true'));
      this.root.layers.push({
        container: this.sheet || this.wrapper,
        onClose: () => this.close(),
        returnFocus: opener || document.activeElement,
        initialFocus: this.closeBtn || null
      });
      this.root.dispatchEvent(new CustomEvent('ss-pdp:layer', { detail: { name: 'variant', open: true } }));
      this.track('variant_popup_open', { legacy: [] });
    }

    close(silent) {
      if (!this.wrapper || !this.isOpen) return;
      this.isOpen = false;
      hide(this.wrapper);
      this.wrapper.setAttribute('aria-hidden', 'true');
      document.body.classList.remove("pv-variant-popup-open");
      this.openers.forEach((o) => o.setAttribute('aria-expanded', 'false'));
      this.root.layers.remove(this.sheet || this.wrapper);
      if (!silent) this.root.dispatchEvent(new CustomEvent('ss-pdp:layer', { detail: { name: 'variant', open: false } }));
    }

    confirm() {
      var custom = null;
      if (this.isCustomSelected()) {
        if (!this.validateCustom(false)) {
          if (this.lengthInput) this.lengthInput.focus();
          return;
        }
        var dims = this.readCustom();
        var heightRaw = this.checkedValue(this.heightRadios);
        custom = { length: dims.length, width: dims.width, height: heightRaw, unit: this.customUnit };
      }
      var variant = this.pendingVariant();
      if (!variant) {
        this.root.toast.show(this.t('variantUnavailable'), 'error');
        return;
      }
      this.root.setVariant(variant, { custom: custom, source: 'picker' });
      this.track('variant_confirm', { variant_id: variant.id, custom: !!custom });
      this.close();
    }

    renderSelected(variant, custom) {
      if (!variant) return;
      var o1 = this.$('#dev_selected_variant_option1');
      var o2 = this.$('#dev_selected_variant_option2');
      var o3 = this.$('#dev_selected_variant_option3');
      if (custom) {
        if (o1) o1.textContent = this.t('customLabel');
        if (o2) o2.textContent = custom.height !== undefined && custom.height !== null ? String(custom.height) : String(this.optionAt(variant, this.heightPos) || '');
        if (o3) o3.textContent = this.formatSize(custom.length, custom.width, custom.unit || this.customUnit, this.displayUnit);
      } else {
        if (o1) o1.textContent = String(this.optionAt(variant, this.catPos) || '');
        if (o2) o2.textContent = String(this.optionAt(variant, this.heightPos) || '');
        if (o3) o3.textContent = this.formatSizeValue(this.optionAt(variant, this.sizePos));
      }
    }

    destroy() {
      super.destroy();
      if (this.isOpen) this.close(true);
    }
  }

  /* ------------------------------------------------------------------ */
  /* 7-9. Quantity, add to cart, sold-out / notify                       */
  /* ------------------------------------------------------------------ */

  class SsPdpCart extends SsPdpModule {
    init() {
      this.qtyInputs = this.$$('.mattress-pdp-qty-input');
      this.quantity = 1;
      this.maxQty = 99;

      this.$$('.mattress-pdp-qty-selector').forEach((selector) => {
        var minus = selector.querySelector('.mattress-pdp-qty-minus');
        var plus = selector.querySelector('.mattress-pdp-qty-plus');
        var input = selector.querySelector('.mattress-pdp-qty-input');
        if (minus) { minus.setAttribute('type', 'button'); minus.setAttribute('aria-label', this.t('decreaseQuantity')); this.on(minus, 'click', (e) => { e.preventDefault(); this.setQuantity(this.quantity - 1); }); }
        if (plus) { plus.setAttribute('type', 'button'); plus.setAttribute('aria-label', this.t('increaseQuantity')); this.on(plus, 'click', (e) => { e.preventDefault(); this.setQuantity(this.quantity + 1); }); }
        if (input) {
          input.setAttribute('aria-label', this.t('quantityLabel'));
          input.setAttribute('inputmode', 'numeric');
          this.on(input, 'change', () => this.setQuantity(input.value));
          this.on(input, 'input', () => { if (input.value !== '') this.setQuantity(input.value, true); });
        }
      });

      this.$$('.mattress-pdp-atc-btn').forEach((btn) => {
        if (btn.hasAttribute('data-ss-variant-confirm')) return;
        btn.setAttribute('type', 'button');
        this.on(btn, 'click', (e) => this.onAtcClick(e, btn));
      });

      this.$$('.notify-btn').forEach((btn) => {
        btn.setAttribute('type', 'button');
        this.on(btn, 'click', (e) => {
          e.preventDefault();
          this.openNotify();
        });
      });

      this.on(this.root, 'ss-pdp:variant', (e) => this.onVariant(e.detail && e.detail.variant));
      if (this.root.currentVariant) this.onVariant(this.root.currentVariant);
    }

    onVariant(variant) {
      if (!variant) return;
      var ruleMax = toNumber(get(variant, 'quantity_rule.max', null), 0);
      this.maxQty = ruleMax > 0 ? ruleMax : 99;
      this.setQuantity(this.quantity);
      var soldOut = !variant.available;
      this.$$('.out_of_stock_card').forEach((el) => show(el, soldOut));
      this.$$('.out_of_stock').forEach((el) => { if (!el.closest('#pv-pdp-variant-full-wrapper')) { el.textContent = this.t('outOfStock'); show(el, soldOut); } });
      this.$$('.mattress-pdp-atc-btn').forEach((btn) => { if (!btn.hasAttribute('data-ss-variant-confirm')) show(btn, !soldOut); });
      this.$$('.notify-btn').forEach((btn) => show(btn, soldOut));
      this.$$('.mattress-pdp-qty-selector').forEach((el) => el.classList.toggle('is-disabled', soldOut));
    }

    setQuantity(value, silentClamp) {
      var min = Math.max(1, toNumber(get(this.root.currentVariant, 'quantity_rule.min', 1), 1));
      var q = Math.floor(toNumber(value, min));
      if (q > this.maxQty) {
        q = this.maxQty;
        if (!silentClamp) this.root.toast.show(this.t('quantityMax', { max: this.maxQty }), 'info');
      }
      if (q < min) q = min;
      this.quantity = q;
      this.qtyInputs.forEach((input) => {
        if (String(input.value) !== String(q)) input.value = q;
        input.min = min;
        input.max = this.maxQty;
      });
      this.root.dispatchEvent(new CustomEvent('ss-pdp:quantity', { detail: { quantity: q } }));
    }

    onAtcClick(e, btn) {
      e.preventDefault();
      var state = btn.getAttribute('data-atc-state');
      if (state === 'pincode-required' || state === 'unserviceable') {
        e.stopPropagation();
        this.track('atc_no_pincode', { legacy: [], state: state });
        this.root.dispatchEvent(new CustomEvent('ss-pdp:atc-blocked', { detail: { state: state } }));
        return;
      }
      if (this.root.delivery && typeof this.root.delivery.beforeAddToCart === 'function' && !this.root.delivery.beforeAddToCart()) {
        return;
      }
      this.addToCart(btn);
    }

    addToCart(btn) {
      var variant = this.root.currentVariant;
      if (!variant || !variant.id) return;
      if (!variant.available) { this.openNotify(); return; }
      if (btn.classList.contains('ss-pdp__loading')) return;

      var addUrl = get(window, 'theme.routes.cart_add_url', '');
      if (!addUrl) { warn('theme.routes.cart_add_url missing'); return; }

      var buttons = this.$$('.mattress-pdp-atc-btn');
      buttons.forEach((b) => { b.classList.add('ss-pdp__loading'); b.setAttribute('aria-disabled', 'true'); b.setAttribute('aria-busy', 'true'); });

      var formData = new FormData();
      formData.append('id', String(variant.id));
      formData.append('quantity', String(this.quantity));
      var custom = this.root.currentCustom;
      if (custom) {
        var unitSuffix = custom.unit ? ' ' + custom.unit : '';
        var lengthProp = ssUnescape(get(this.config, 'customSize.lengthProperty', 'Length'));
        var widthProp = ssUnescape(get(this.config, 'customSize.widthProperty', 'Width'));
        var heightProp = ssUnescape(get(this.config, 'customSize.heightProperty', 'Height'));
        if (Number.isFinite(custom.length)) formData.append('properties[' + lengthProp + ']', this.formatNumber(custom.length, 1) + unitSuffix);
        if (Number.isFinite(custom.width)) formData.append('properties[' + widthProp + ']', this.formatNumber(custom.width, 1) + unitSuffix);
        if (custom.height) formData.append('properties[' + heightProp + ']', String(custom.height));
      }
      var miniCart = document.querySelector('mini-cart');
      if (miniCart && typeof miniCart.getSectionsToRender === 'function') {
        formData.append('sections', miniCart.getSectionsToRender().map((s) => s.id).join(','));
        formData.append('sections_url', window.location.pathname);
        if (typeof miniCart.setActiveElement === 'function') miniCart.setActiveElement(btn);
      }

      this.track('add_to_cart', { variant_id: variant.id, quantity: this.quantity, value: toNumber(variant.price, 0) / 100, currency: this.config.currency || '' });

      request(addUrl, {
        method: 'POST',
        headers: { 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      })
        .then((res) => {
          var data = res.data || {};
          if (!res.ok || data.status) {
            var message = data.description || data.message || this.t('cartError');
            if (ssPubSubEvents()) {
              ssPublish(ssPubSubEvents().cartError, { source: 'ss-pdp-main-banner', productVariantId: variant.id, errors: data.errors || data.description, message: data.message });
            }
            this.showError(message);
            return;
          }
          if (ssPubSubEvents()) {
            ssPublish(ssPubSubEvents().cartUpdate, { source: 'ss-pdp-main-banner', productVariantId: variant.id, cartData: data });
          }
          document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: data } }));
          if (miniCart && typeof miniCart.renderContents === 'function' && data.sections) {
            miniCart.renderContents(data);
            if (miniCart.classList.contains('is-empty')) miniCart.classList.remove('is-empty');
            this.root.toast.show(this.t('addedToCart'), 'success');
          } else {
            window.location = get(window, 'theme.routes.cart_url', '/cart');
          }
        })
        .catch((err) => { warn('add to cart', err); this.showError(this.t('cartError')); })
        .finally(() => {
          buttons.forEach((b) => { b.classList.remove('ss-pdp__loading'); b.removeAttribute('aria-disabled'); b.removeAttribute('aria-busy'); });
        });
    }

    showError(message) {
      var box = this.$('.product-form__error-message');
      if (box) {
        box.textContent = message;
        box.setAttribute('role', 'alert');
        show(box);
        this.setTimeout(() => hide(box), 6000);
      }
      this.root.toast.show(message, 'error');
    }

    openNotify() {
      var variant = this.root.currentVariant;
      if (this.root.variantPicker && this.root.variantPicker.isOpen) this.root.variantPicker.close();
      document.body.classList.add('dev_acb-bottomsheet-open');
      this.track('notify_me_OOS', { legacy: [], variant_id: variant ? variant.id : '' });
      document.dispatchEvent(new CustomEvent('ss-pdp:notify', {
        bubbles: true,
        detail: { variant: variant, product: this.config.product || {}, custom: this.root.currentCustom || null }
      }));
    }

    destroy() {
      super.destroy();
      document.body.classList.remove('dev_acb-bottomsheet-open');
    }
  }

  /* ------------------------------------------------------------------ */
  /* 10. Offers (header link, popup, T&C accordion, marquee, expiry)     */
  /* ------------------------------------------------------------------ */

  class SsPdpOffers extends SsPdpModule {
    init() {
      if (get(this.config, 'features.offers', true) === false) return;
      this.popup = this.$('.pdp-dynamic-popup');
      this.overlay = this.$('.pdp-dynamic-popup-overlay');
      this.closeBtn = this.$('#closeSidebar');
      this.openLink = this.$('.pdp-dynamic-ofer-header a');
      this.isOpen = false;

      if (this.popup) {
        this.popup.setAttribute('role', 'dialog');
        this.popup.setAttribute('aria-modal', 'true');
        hide(this.popup);
        if (this.overlay) hide(this.overlay);
        this.popup.setAttribute('aria-hidden', 'true');
        var title = this.popup.querySelector('.pdp-dc-header-title p');
        if (title) { if (!title.id) title.id = 'ss-pdp-offers-title'; this.popup.setAttribute('aria-labelledby', title.id); }
      }
      if (this.openLink) {
        this.openLink.setAttribute('role', 'button');
        this.openLink.setAttribute('tabindex', '0');
        this.openLink.setAttribute('aria-haspopup', 'dialog');
        this.openLink.setAttribute('aria-expanded', 'false');
        this.on(this.openLink, 'click', (e) => { e.preventDefault(); this.open(); });
        this.on(this.openLink, 'keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.open(); } });
      }
      if (this.closeBtn) {
        this.closeBtn.setAttribute('role', 'button');
        this.closeBtn.setAttribute('tabindex', '0');
        this.closeBtn.setAttribute('aria-label', this.t('close'));
        this.on(this.closeBtn, 'click', (e) => { e.preventDefault(); this.close(); });
        this.on(this.closeBtn, 'keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.close(); } });
      }
      if (this.overlay) this.on(this.overlay, 'click', () => this.close());

      // T&C accordion
      this.$$('.pdp-offer-item-tc').forEach((toggle, i) => {
        var item = toggle.closest('.pdp-offers-list-item') || toggle.parentElement;
        var content = item ? item.querySelector('.pdp-offer-item-tc-content') : null;
        if (!content) return;
        if (!content.id) content.id = 'ss-pdp-offer-tc-' + i;
        toggle.setAttribute('role', 'button');
        toggle.setAttribute('tabindex', '0');
        toggle.setAttribute('aria-expanded', 'false');
        toggle.setAttribute('aria-controls', content.id);
        content.setAttribute("aria-hidden", "true");
        var flip = () => {
          var open = !toggle.classList.contains("is-open");
          toggle.classList.toggle("is-open", open);
          content.classList.toggle("is-open", open);
          if (item) item.classList.toggle("is-open", open);
          content.setAttribute("aria-hidden", open ? "false" : "true");
          toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
          var caret = toggle.querySelector('span, svg');
          if (caret) caret.classList.toggle('is-rotated', open);
        };
        this.on(toggle, 'click', (e) => { e.preventDefault(); flip(); });
        this.on(toggle, 'keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); flip(); } });
      });

      // Coupon / bank cards analytics (port of coupons_section)
      this.$$('.pdp-diwali-offers .diwali-offer-card').forEach((card) => {
        this.on(card, 'click', () => this.track('coupons_section', { legacy: [card.dataset.bank || ''], bank: card.dataset.bank || '' }));
      });

      this.renderExpiry();
      this.initMarquee();
    }

    renderExpiry() {
      var today = startOfDay(new Date());
      var defaultExpiry = toLocalDate(get(this.config, 'offers.defaultExpiry', ''));
      this.$$('.pdp-offer-item-expire').forEach((el) => {
        var expiry = toLocalDate(el.dataset.expiry || '') || defaultExpiry;
        if (!expiry) { return; }
        var days = daysBetween(today, expiry);
        var item = el.closest('.pdp-offers-list-item') || el.closest('.pdp-offer-card') || null;
        if (days < 0) {
          if (item) item.remove(); else hide(el);
          return;
        }
        if (days === 0) el.textContent = this.t('offerEndsToday');
        else if (days === 1) el.textContent = this.t('offerEndsTomorrow');
        else el.textContent = this.t('offerEndsInDays', { days: days });
        el.setAttribute('datetime', expiry.toISOString().slice(0, 10));
      });
      var list = this.$('.pdp-offers-list-wrapper');
      if (list && !list.querySelector('.pdp-offers-list-item')) {
        var empty = list.querySelector('.ss-pdp__empty-state');
        if (!empty) {
          empty = document.createElement('p');
          empty.className = 'ss-pdp__empty-state';
          list.appendChild(empty);
        }
        empty.textContent = this.t('noOffers');
      }
    }

    initMarquee() {
      var track = this.$('.offer-slider-track');
      if (!track || track.children.length < 2 || prefersReducedMotion()) return;
      var wrapper = track.parentElement;
      var paused = false;
      this.marqueePos = 0;
      var speed = 0.5; // px per frame
      track.classList.add('is-marquee');
      var clone = Array.from(track.children).map((c) => c.cloneNode(true));
      clone.forEach((c) => { c.setAttribute('aria-hidden', 'true'); track.appendChild(c); });
      var half = () => track.scrollWidth / 2;
      var step = () => {
        if (!paused && this.root.isConnected) {
          this.marqueePos += speed;
          if (this.marqueePos >= half()) this.marqueePos = 0;
          track.style.transform = "translateX(" + (-this.marqueePos) + "px)";
        }
        this.raf = window.requestAnimationFrame(step);
      };
      ['mouseenter', 'touchstart', 'focusin'].forEach((evt) => this.on(wrapper || track, evt, () => { paused = true; }, { passive: true }));
      ['mouseleave', 'touchend', 'focusout'].forEach((evt) => this.on(wrapper || track, evt, () => { paused = false; }, { passive: true }));
      this.raf = window.requestAnimationFrame(step);
    }

    remeasure() {
      var track = this.$(".offer-slider-track");
      if (!track) return;
      this.marqueePos = 0;
      track.style.transform = "";
    }

    open() {
      if (!this.popup || this.isOpen) return;
      this.isOpen = true;
      show(this.popup);
      if (this.overlay) { show(this.overlay); this.overlay.classList.add("active"); }
      this.popup.classList.add("active");
      this.popup.setAttribute("aria-hidden", "false");
      if (this.openLink) this.openLink.setAttribute('aria-expanded', 'true');
      scrollLock.lock();
      this.root.layers.push({ container: this.popup, onClose: () => this.close(), initialFocus: this.closeBtn || null });
      this.track('offers_view_all', { legacy: ['PDP'] });
    }

    close() {
      if (!this.popup || !this.isOpen) return;
      this.isOpen = false;
      this.popup.classList.remove("active");
      this.popup.setAttribute("aria-hidden", "true");
      if (this.overlay) this.overlay.classList.remove("active");
      this.setTimeout(() => { if (!this.isOpen) { hide(this.popup); if (this.overlay) hide(this.overlay); } }, 300);
      if (this.openLink) this.openLink.setAttribute('aria-expanded', 'false');
      scrollLock.unlock();
      this.root.layers.remove(this.popup);
    }

    destroy() {
      super.destroy();
      if (this.raf) window.cancelAnimationFrame(this.raf);
      if (this.isOpen) this.close();
    }
  }

  /* ------------------------------------------------------------------ */
  /* 11. Offer timer (countdown to dispatch cut-off)                     */
  /* ------------------------------------------------------------------ */

  class SsPdpOfferTimer extends SsPdpModule {
    init() {
      if (get(this.config, 'features.timer', true) === false) return;
      this.wrap = this.$('#product-offer-timer');
      if (!this.wrap) return;
      var cutoff = parseClockTime(get(this.config, 'delivery.dispatchCutoff', ''));
      if (!cutoff) { hide(this.wrap); return; }
      this.heading = this.wrap.querySelector('.product-offer-timer-heading');
      this.timeEl = this.wrap.querySelector('time');
      if (!this.timeEl) {
        this.timeEl = document.createElement('time');
        this.timeEl.className = 'product-offer-timer-value';
        this.wrap.appendChild(this.timeEl);
      }
      this.wrap.setAttribute('aria-live', 'off');
      this.timeEl.setAttribute('aria-live', 'off');
      this.target = new Date();
      this.target.setHours(cutoff.hours, cutoff.minutes, 0, 0);
      this.tick();
      this.setInterval(() => this.tick(), 1000);
    }

    tick() {
      var diff = this.target.getTime() - Date.now();
      if (diff <= 0) { hide(this.wrap); this.clearTimers(); return; }
      var total = Math.floor(diff / 1000);
      var h = Math.floor(total / 3600), m = Math.floor((total % 3600) / 60), s = total % 60;
      var pad = (n) => String(n).padStart(2, '0');
      var text = pad(h) + ':' + pad(m) + ':' + pad(s);
      if (this.heading) this.heading.textContent = this.t('timerHeading', { time: this.formatDate(this.target, { hour: '2-digit', minute: '2-digit' }) });
      this.timeEl.textContent = text;
      this.timeEl.setAttribute('datetime', 'PT' + h + 'H' + m + 'M' + s + 'S');
      this.timeEl.setAttribute('aria-label', this.t('timerRemaining', { hours: h, minutes: m }));
      show(this.wrap);
    }
  }

  /* ------------------------------------------------------------------ */
  /* 12. Delivery widget: postal code, EDD, installation, ATC gating     */
  /* ------------------------------------------------------------------ */

  class SsPdpDelivery extends SsPdpModule {
    init() {
      this.enabled = get(this.config, 'features.delivery', true) !== false;
      this.container = this.$('.custom-pincode-container');
      this.restricted = !!get(this.config, 'delivery.atcRequiresPostalCode', false);
      this.serviceable = false;
      this.eddUrl = get(this.config, 'endpoints.edd', '');
      this.geocodeUrl = get(this.config, 'endpoints.geocode', '');
      this.installUrl = get(this.config, 'endpoints.installation', '');
      this.eddKey = get(this.config, 'apiKeys.edd', '');
      this.storageKey = get(this.config, 'postalCode.storageKey', 'ssPdpPostalCode');
      this.ttl = toNumber(get(this.config, 'postalCode.ttlMinutes', 30), 30);
      try { this.pattern = new RegExp(get(this.config, 'postalCode.pattern', '^.{3,10}$')); } catch (e) { this.pattern = /^.{3,10}$/; }
      this.nextDayMaxHours = toNumber(get(this.config, 'delivery.nextDayMaxHours', 24), 24);
      this.promoUntil = toLocalDate(get(this.config, 'delivery.promoLabelUntil', ''));
      this.requestSeq = 0;

      if (!this.enabled || !this.container) {
        if (this.container) hide(this.container);
        return;
      }

      this.input = this.$('#pincode-input');
      this.checkBtn = this.$('#check-btn');
      this.checkLoader = this.$('#check-loader');
      this.changeBtn = this.$('[data-change-button]');
      this.changeLoader = this.$('#change-loader');
      this.inputContainers = this.$$('[data-input-container]');
      this.inputBox = this.$('[data-input-box]');
      this.staticBox = this.$('[data-static-box]');
      this.errorBox = this.$('[data-error-box]');
      this.errorMsg = this.errorBox ? this.errorBox.querySelector('.error-message') : null;
      this.deliveryContainer = this.$('[data-delivery-container]');
      this.successBox = this.$('[data-success-box]');
      this.successEstimate = this.$('[data-success-estimate]');
      this.successCode = this.$('[data-success-pincode]');
      this.successInfo = this.$('[data-success-pincode-info]');
      this.dateRange = this.$('[data-date-range]');
      this.installNote = this.$('.delivery-texts .install-note');
      this.storeBox = this.$('[data-store-box]');
      this.deliveryBox = this.$('[data-delivery-box]');
      this.promoLabels = this.$$('.diwali-label');
      this.nextDayBanners = this.$$('.next-day-widget-banner');

      if (this.input) {
        var maxLength = toNumber(get(this.config, 'postalCode.maxLength', 0), 0);
        if (maxLength > 0) this.input.setAttribute('maxlength', String(maxLength));
        this.input.setAttribute('autocomplete', 'postal-code');
        this.input.setAttribute('inputmode', 'text');
        this.on(this.input, 'keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); this.onCheck(); } });
        this.on(this.input, 'input', () => { if (this.errorBox && isVisible(this.errorBox)) hide(this.errorBox); });
      }
      if (this.checkBtn) {
        this.checkBtn.setAttribute('type', 'button');
        this.on(this.checkBtn, 'click', (e) => { e.preventDefault(); this.onCheck(); });
      }
      if (this.changeBtn) {
        this.changeBtn.setAttribute('type', 'button');
        this.on(this.changeBtn, 'click', (e) => { e.preventDefault(); this.showInputSection(); this.track('edd_change', { legacy: [] }); if (this.input) this.input.focus(); });
      }
      if (this.errorBox) this.errorBox.setAttribute('role', 'alert');
      if (this.dateRange) { this.dateRange.setAttribute('aria-live', 'polite'); }
      if (this.storeBox) hide(this.storeBox);
      this.nextDayBanners.forEach((b) => hide(b));

      this.on(this.root, 'ss-pdp:variant', debounce(() => this.refreshOnVariantChange(), 300));
      this.on(this.root, 'ss-pdp:atc-blocked', () => this.scrollToSection());

      var saved = this.loadCode();
      if (saved) {
        if (this.input) this.input.value = saved;
        this.handleEDD(saved, {});
      } else {
        this.showInputSection();
        if (this.restricted) this.setPincodeRequiredState();
      }
    }

    /* ---- storage / validation ---- */

    isValid(code) { return !!code && this.pattern.test(String(code).trim()); }
    saveCode(code) { storage.set(this.storageKey, String(code), this.ttl); }
    loadCode() {
      var v = storage.get(this.storageKey);
      return typeof v === 'string' && v ? v : null;
    }
    clearCode() { storage.remove(this.storageKey); }

    /* ---- UI states ---- */

    setLoading(loading) {
      if (this.checkBtn) show(this.checkBtn, !loading);
      if (this.checkLoader) show(this.checkLoader, loading);
      if (this.input) this.input.setAttribute('aria-busy', loading ? 'true' : 'false');
    }

    setChangeLoading(loading) {
      if (this.changeBtn) show(this.changeBtn, !loading);
      if (!this.changeLoader && loading && this.changeBtn) {
        this.changeLoader = document.createElement('div');
        this.changeLoader.id = 'change-loader';
        this.changeLoader.className = 'check-loader';
        this.changeLoader.innerHTML = '<div class="pincode-spinner"></div>';
        this.changeBtn.parentNode.insertBefore(this.changeLoader, this.changeBtn);
      }
      if (this.changeLoader) show(this.changeLoader, loading);
    }

    showInputSection() {
      this.inputContainers.forEach((c) => show(c));
      if (this.inputBox) show(this.inputBox);
      if (this.staticBox) show(this.staticBox);
      if (this.deliveryContainer) hide(this.deliveryContainer);
      if (this.successBox) hide(this.successBox);
      if (this.successEstimate) hide(this.successEstimate);
      if (this.errorBox) hide(this.errorBox);
      if (this.input) this.input.value = '';
      this.nextDayBanners.forEach((b) => hide(b));
      this.setLoading(false);
      this.setChangeLoading(false);
      if (this.restricted) { this.serviceable = false; this.clearAtcGate(); }
      this.root.dispatchEvent(new CustomEvent('ss-pdp:delivery', { detail: { state: 'input' } }));
    }

    showError(message) {
      this.setLoading(false);
      this.setChangeLoading(false);
      if (this.errorMsg) this.errorMsg.textContent = message || this.t('deliveryGenericError');
      if (this.errorBox) show(this.errorBox);
      this.inputContainers.forEach((c) => show(c));
      if (this.inputBox) show(this.inputBox);
      if (this.staticBox) hide(this.staticBox);
      if (this.deliveryContainer) hide(this.deliveryContainer);
      if (this.successBox) hide(this.successBox);
      if (this.successEstimate) hide(this.successEstimate);
      this.nextDayBanners.forEach((b) => hide(b));
      this.root.dispatchEvent(new CustomEvent('ss-pdp:delivery', { detail: { state: 'error', message: message } }));
    }

    showSuccess(code, minDate, maxDate, isExpress, cityState) {
      this.setLoading(false);
      this.setChangeLoading(false);
      var eddMsg = '';
      var hours = toNumber(minDate, 0);

      if (isExpress) {
        if (this.dateRange) this.dateRange.textContent = this.t('expressDelivery', { hours: hours });
        eddMsg = this.t('expressDelivery', { hours: hours });
        this.showPromoLabel(this.promoUntil ? startOfDay(new Date()) <= this.promoUntil : false);
        this.updateNextDayBanners(hours > 0 && hours <= this.nextDayMaxHours);
      } else if (minDate || maxDate) {
        var min = toLocalDate(minDate), max = toLocalDate(maxDate) || min;
        var range = '';
        if (min && max && daysBetween(min, max) !== 0) range = this.formatDate(min) + ' - ' + this.formatDate(max);
        else if (min) range = this.formatDate(min);
        if (this.dateRange) this.dateRange.textContent = this.t('estimatedDelivery', { range: range });
        eddMsg = range;
        this.showPromoLabel(!!(this.promoUntil && max && max <= this.promoUntil));
        this.updateNextDayBanners(false);
      } else {
        if (this.dateRange) this.dateRange.textContent = '';
        this.showPromoLabel(false);
        this.updateNextDayBanners(false);
      }

      if (this.successBox) show(this.successBox);
      if (this.deliveryContainer) show(this.deliveryContainer);
      if (this.successEstimate) show(this.successEstimate);
      if (this.successCode) this.successCode.textContent = code;
      if (this.successInfo) {
        var info = cityState ? [cityState.city, cityState.state].filter(Boolean).join(', ') : '';
        this.successInfo.textContent = info;
        show(this.successInfo, !!info);
      }
      this.inputContainers.forEach((c) => hide(c));
      if (this.inputBox) hide(this.inputBox);
      if (this.errorBox) hide(this.errorBox);
      if (this.staticBox) hide(this.staticBox);

      if (eddMsg) this.track('EDD', { legacy: [code, eddMsg], postal_code: code, edd: eddMsg });
      this.root.dispatchEvent(new CustomEvent('ss-pdp:delivery', { detail: { state: 'success', code: code, min: minDate, max: maxDate, express: !!isExpress, city: cityState } }));
    }

    showPromoLabel(visible) {
      this.promoLabels.forEach((label) => {
        show(label, visible);
        var estimate = label.closest('[data-success-estimate]');
        if (estimate) estimate.classList.toggle('show-diwali-label', visible);
      });
    }

    updateNextDayBanners(visible) {
      var enabled = get(this.config, 'features.nextDay', false) !== false;
      this.nextDayBanners.forEach((banner) => {
        var maxHours = toNumber(banner.dataset.nextDayWidget, this.nextDayMaxHours);
        show(banner, enabled && visible && maxHours >= this.nextDayMaxHours);
      });
    }

    scrollToSection() {
      if (this.container) this.container.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'center' });
    }

    /* ---- ATC gating (port of recliner-atc-strip logic) ---- */

    atcButtons() {
      return this.$$('.mattress-pdp-atc-btn').filter((b) => !b.hasAttribute('data-ss-variant-confirm'));
    }

    injectAtcMessage() {
      if (this.$('.recliner-atc-strip')) return;
      this.atcButtons().forEach((btn) => {
        if (btn.closest('.sticky-stc-mobile-wrap')) return;
        var msg = document.createElement('div');
        msg.className = 'recliner-atc-strip';
        msg.setAttribute('role', 'status');
        var wrapper = btn.closest('.pv-variant-atc-wrapper') || btn.closest('.mattress-pdp-quantity-wrap') || btn.parentElement;
        wrapper.insertAdjacentElement('afterend', msg);
      });
    }

    showAtcMessage(type) {
      this.injectAtcMessage();
      var text = type === 'unserviceable' ? this.t('atcUnserviceable') : this.t('atcPostalCodeRequired');
      this.$$('.recliner-atc-strip').forEach((strip) => { strip.textContent = text; show(strip); });
    }

    hideAtcMessage() { this.$$('.recliner-atc-strip').forEach((strip) => hide(strip)); }

    setAtcButtons(addClass, removeClass, state) {
      this.atcButtons().forEach((btn) => {
        if (removeClass) removeClass.split(' ').forEach((c) => btn.classList.remove(c));
        if (addClass) addClass.split(' ').forEach((c) => btn.classList.add(c));
        if (state === null) btn.removeAttribute('data-atc-state');
        else if (state !== undefined) btn.setAttribute('data-atc-state', state);
      });
    }

    setPincodeRequiredState() {
      this.setAtcButtons('atc-pincode-required', 'atc-restricted-blocked', 'pincode-required');
      this.showAtcMessage('pincode-required');
    }

    setUnserviceableState() {
      this.setAtcButtons('atc-restricted-blocked', 'atc-pincode-required', 'unserviceable');
      this.showAtcMessage('unserviceable');
    }

    clearAtcGate() {
      this.setAtcButtons(null, 'atc-pincode-required atc-restricted-blocked', null);
      this.hideAtcMessage();
    }

    /** Called by the cart module before adding. Returns false to block. */
    beforeAddToCart() {
      if (!this.enabled || !this.restricted) return true;
      if (this.serviceable) return true;
      this.track('atc_no_pincode', { legacy: [] });
      this.setPincodeRequiredState();
      this.scrollToSection();
      return false;
    }

    /* ---- flows ---- */

    onCheck() {
      var code = this.input ? this.input.value.trim() : '';
      if (this.isValid(code)) this.track('atc_pincode', { legacy: [code], postal_code: code });
      this.handleEDD(code, {});
    }

    refreshOnVariantChange() {
      var saved = this.loadCode();
      if (!saved) return;
      this.setChangeLoading(true);
      this.handleEDD(saved, { silent: true });
    }

    handleEDD(savedCode, opts) {
      var options = opts || {};
      var code = String((options.fromSidebar ? savedCode : (this.input && this.input.value) || savedCode) || '').trim();
      var seq = ++this.requestSeq;
      var variant = this.root.currentVariant;

      if (!this.isValid(code)) {
        this.showInputSection();
        this.showError(this.t('invalidPostalCode'));
        if (options.fromSidebar && this.root.stores) this.root.stores.showSidebarError(this.t('invalidPostalCode'));
        return Promise.resolve(false);
      }
      this.setLoading(true);

      return this.geocode(code).then((cityState) => {
        if (seq !== this.requestSeq) return false;
        if (!cityState) {
          this.showInputSection();
          this.showError(this.t('invalidPostalCode'));
          if (options.fromSidebar && this.root.stores) this.root.stores.showSidebarError(this.t('invalidPostalCode'));
          return false;
        }
        if (!this.eddUrl) {
          return this.applyFallback(code, cityState, variant);
        }
        return this.fetchEdd(code, variant).then((res) => {
          if (seq !== this.requestSeq) return false;
          return this.applyEddResponse(res, code, cityState, variant);
        });
      }).catch((err) => {
        warn('EDD', err);
        if (this.restricted) { this.serviceable = false; this.setPincodeRequiredState(); }
        this.showError(this.t('deliveryGenericError'));
        return false;
      });
    }

    applyFallback(code, cityState, variant) {
      var minDays = toNumber(get(this.config, 'delivery.businessDaysMin', 3), 3);
      var maxDays = toNumber(get(this.config, 'delivery.businessDaysMax', 6), 6);
      var now = new Date();
      var cutoff = parseClockTime(get(this.config, 'delivery.dispatchCutoff', ''));
      var start = new Date(now.getTime());
      if (cutoff && now.getHours() * 60 + now.getMinutes() >= cutoff.totalMinutes) start.setDate(start.getDate() + 1);
      var min = addBusinessDays(start, minDays);
      var max = addBusinessDays(start, Math.max(minDays, maxDays));
      if (variant && !variant.available) {
        // Still show the location, but no estimate for sold-out variants.
        this.showSuccess(code, null, null, false, cityState);
        if (this.successEstimate) hide(this.successEstimate);
      } else {
        this.showSuccess(code, min, max, false, cityState);
      }
      if (this.installNote) this.installNote.textContent = this.t('deliveryNote');
      this.serviceable = true;
      this.clearAtcGate();
      this.saveCode(code);
      if (this.root.stores) this.root.stores.fetchStores(code, false);
      return true;
    }

    geocode(code) {
      if (!this.geocodeUrl) return Promise.resolve({ city: '', state: '' });
      var url = this.geocodeUrl.indexOf('{code}') !== -1
        ? this.geocodeUrl.replace('{code}', encodeURIComponent(code))
        : this.geocodeUrl.replace(/\/?$/, '/') + encodeURIComponent(code);
      var headers = { 'Accept': 'application/json' };
      if (this.eddKey) headers['x-api-key'] = this.eddKey;
      return request(url, { method: 'GET', headers: headers }).then((res) => {
        var data = get(res, 'data.data', res.data);
        if (!data) return null;
        var results = Array.isArray(data.results) ? data.results : [];
        if (data.status && data.status !== 'OK') return null;
        if (!results.length) {
          if (data.city || data.state || data.region) return { city: data.city || '', state: data.state || data.region || '' };
          return null;
        }
        var components = get(results[0], 'address_components', []);
        var country = String(this.config.country || '').toUpperCase();
        if (country) {
          var matchesCountry = components.some((c) => (c.types || []).indexOf('country') !== -1 && String(c.short_name || '').toUpperCase() === country);
          if (components.length && !matchesCountry) return null;
        }
        var city = '', state = '';
        components.forEach((c) => {
          var types = c.types || [];
          if (types.indexOf('administrative_area_level_1') !== -1) state = c.long_name || state;
          if (types.indexOf('locality') !== -1 || types.indexOf('postal_town') !== -1) city = c.long_name || city;
        });
        return { city: city, state: state };
      }).catch((e) => { warn('geocode', e); return null; });
    }

    fetchEdd(code, variant) {
      var headers = jsonHeaders();
      if (this.eddKey) headers['x-api-key'] = this.eddKey;
      var payload = {
        orderDate: new Date().toISOString().split('T')[0],
        customerPincode: code,
        customerPostalCode: code,
        countryCode: this.config.country || '',
        isWhiteGloveOrder: false,
        consolidatedShipment: false,
        orderType: 'PREPAID',
        source: 'WEBSITE',
        subSource: 'PDP',
        lineItems: [{
          skuCode: variant ? variant.sku || '' : '',
          variantId: variant ? String(variant.id) : '',
          quantity: 1,
          unitPrice: variant ? toNumber(variant.price, 0) / 100 : 0
        }]
      };
      return request(this.eddUrl, { method: 'POST', headers: headers, body: JSON.stringify(payload) }).then((res) => res.data || {});
    }

    extractRestrictedFields(shipment) {
      var truthy = (v) => [1, '1', true, 'true'].indexOf(v) !== -1;
      var s = shipment || {};
      return {
        isRestrictedProduct: truthy(s.isRestrictedProduct),
        isCategoryDelivery: truthy(s.isCategoryDelivery),
        isCategoryInstallation: truthy(s.isCategoryInstallation),
        categoryDelCharge: toNumber(s.categoryDelCharge, 0),
        categoryInstCharge: toNumber(s.categoryInstCharge, 0),
        category: s.category || null
      };
    }

    applyEddResponse(resObj, code, cityState, variant) {
      var shipments = get(resObj, 'data.shipments', []);
      if (!resObj.success || !resObj.data || !shipments.length) {
        if (this.restricted) {
          this.serviceable = false;
          this.showSuccess(code, null, null, false, cityState);
          if (this.successEstimate) hide(this.successEstimate);
          this.setUnserviceableState();
          this.saveCode(code);
        } else {
          this.showError(this.t('deliveryUnavailable'));
        }
        return false;
      }
      var isExpress = false, expressHours = 48;
      shipments.forEach((s) => {
        if (s.isXpressDelivery) isExpress = true;
        if (s.xpressDeliveryHours) expressHours = toNumber(s.xpressDeliveryHours, expressHours);
      });
      var first = shipments[0] || {};
      var restrictedData = this.extractRestrictedFields(first);
      var variantId = variant ? String(variant.id) : '';
      var productId = String(get(this.config, 'product.id', ''));

      if (restrictedData.isRestrictedProduct) {
        if (!restrictedData.isCategoryDelivery) {
          this.serviceable = false;
          this.showSuccess(code, null, null, false, cityState);
          if (this.successEstimate) hide(this.successEstimate);
          this.setUnserviceableState();
          this.saveCode(code);
          return false;
        }
        this.serviceable = true;
        this.clearAtcGate();
        if (isExpress) this.showSuccess(code, expressHours, '', true, cityState);
        else this.showSuccess(code, resObj.data.minEdd, resObj.data.maxEdd, false, cityState);
        this.fetchInstallationMaxTat(code, productId, variantId).then((maxTAT) => {
          this.applyRestrictedInstallMessage(restrictedData, Object.assign({}, first, { maxTAT: maxTAT }));
        });
        this.saveCode(code);
        if (this.root.stores) this.root.stores.fetchStores(code, false);
        return true;
      }

      if (this.restricted) { this.serviceable = true; this.clearAtcGate(); }
      this.serviceable = true;
      if (isExpress) this.showSuccess(code, expressHours, '', true, cityState);
      else this.showSuccess(code, resObj.data.minEdd, resObj.data.maxEdd, false, cityState);
      if (variant && !variant.available && this.successEstimate) hide(this.successEstimate);
      this.getInstallation(code, productId, variantId);
      this.saveCode(code);
      if (this.root.stores) this.root.stores.fetchStores(code, false);
      return true;
    }

    /* ---- installation notes (port of 2113-2200) ---- */

    formatInstallTat(maxTAT) {
      var tat = toNumber(maxTAT, 0);
      if (!tat) return null;
      return tat <= 3 ? this.t('hoursCount', { count: tat * 24 }) : this.t('daysCount', { count: tat });
    }

    setInstallNote(message) { if (this.installNote) this.installNote.textContent = message; }

    setInstallNoteWithCharges(mainText, deliveryCharge, instCharge) {
      if (!this.installNote) return;
      var html = '';
      if (mainText) html += '<div class="install-main-text">' + escapeHtml(mainText) + '</div>';
      var charges = [];
      if (deliveryCharge > 0) charges.push({ label: this.t('deliveryChargeLabel'), value: this.t('chargeMayApply', { amount: this.formatMoney(deliveryCharge * 100) }) });
      if (instCharge > 0) charges.push({ label: this.t('installationChargeLabel'), value: this.t('chargeMayApply', { amount: this.formatMoney(instCharge * 100) }) });
      if (charges.length) {
        html += '<div class="install-charge-list">';
        charges.forEach((c) => {
          html += '<div class="install-charge-item"><span class="install-charge-label">' + escapeHtml(c.label) + '</span><span class="install-charge-value"> ' + escapeHtml(c.value) + '</span></div>';
        });
        html += '</div>';
      }
      this.installNote.innerHTML = html;
    }

    applyRestrictedInstallMessage(restrictedData, shipment) {
      var d = restrictedData || {};
      var fallbackMessage = this.t('deliveryFallbackNote');
      if (!d.isCategoryDelivery) return;
      var installationNotRequired = d.categoryInstCharge === -1;
      var tatText = this.formatInstallTat(shipment && shipment.maxTAT);
      var installText = tatText
        ? (d.categoryInstCharge > 0 ? this.t('installationWithin', { tat: tatText }) : this.t('freeInstallationWithin', { tat: tatText }))
        : null;
      if (d.categoryDelCharge <= 0 && installationNotRequired) { this.setInstallNote(this.t('deliveryNote')); return; }
      if (d.categoryDelCharge <= 0 && !d.isCategoryInstallation && d.categoryInstCharge === 0) { this.setInstallNote(this.t('installationNotAvailable')); return; }
      if (d.categoryDelCharge <= 0 && d.isCategoryInstallation && d.categoryInstCharge === 0) { this.setInstallNoteWithCharges(installText || fallbackMessage, 0, 0); return; }
      if (d.categoryDelCharge <= 0 && d.isCategoryInstallation && d.categoryInstCharge > 0) { this.setInstallNoteWithCharges(installText, 0, d.categoryInstCharge); return; }
      if (d.categoryDelCharge > 0 && installationNotRequired) { this.setInstallNoteWithCharges(null, d.categoryDelCharge, 0); return; }
      if (d.categoryDelCharge > 0 && d.isCategoryInstallation && d.categoryInstCharge === 0) { this.setInstallNoteWithCharges(installText, d.categoryDelCharge, 0); return; }
      if (d.categoryDelCharge > 0 && d.isCategoryInstallation && d.categoryInstCharge > 0) { this.setInstallNoteWithCharges(installText, d.categoryDelCharge, d.categoryInstCharge); return; }
      if (d.categoryDelCharge > 0 && !d.isCategoryInstallation && d.categoryInstCharge === 0) { this.setInstallNoteWithCharges(null, d.categoryDelCharge, 0); return; }
      this.setInstallNote(fallbackMessage);
    }

    fetchInstallationMaxTat(code, productId, variantId) {
      if (!this.installUrl) return Promise.resolve(null);
      return request(this.installUrl, {
        method: 'POST',
        headers: jsonHeaders(),
        body: JSON.stringify({ pincode: code, postalCode: code, productId: productId, variantId: variantId })
      }).then((res) => {
        var data = res.data || {};
        if (!data.isAllowedJob || data.maxTAT === null || data.maxTAT === undefined) return null;
        return data.maxTAT;
      }).catch(() => null);
    }

    getInstallation(code, productId, variantId) {
      if (!this.installNote) return;
      if (!this.installUrl) { this.installNote.textContent = this.t('deliveryNote'); return; }
      this.fetchInstallationMaxTat(code, productId, variantId).then((maxTAT) => {
        if (maxTAT === null) { this.installNote.textContent = this.t('deliveryFallbackNote'); return; }
        var tatText = this.formatInstallTat(maxTAT);
        this.installNote.textContent = tatText ? this.t('freeInstallationWithin', { tat: tatText }) : this.t('deliveryFallbackNote');
      });
    }
  }

  /* ------------------------------------------------------------------ */
  /* 13. Store sidebar: list, tabs, map (two-click consent)              */
  /* ------------------------------------------------------------------ */

  class SsPdpStores extends SsPdpModule {
    init() {
      this.enabled = get(this.config, 'features.stores', true) !== false;
      this.overlay = this.$('.store-sidebar-overlay');
      this.openers = this.$$('.view-link, .store_near_me_wapp');
      if (!this.enabled || !this.overlay) {
        this.openers.forEach((o) => hide(o));
        if (this.overlay) hide(this.overlay);
        return;
      }
      this.wrapper = this.overlay.querySelector('.store-sidebar-wrapper');
      this.list = this.$('#storeSidebarList');
      this.mapWrap = this.$('#storeSidebarMap');
      this.mapCanvas = this.$('#mapCanvas');
      this.input = this.$('#storePincodeInput');
      this.checkBtn = this.$('#store-check-btn');
      this.loader = this.$('#sidebar-loader');
      this.errorText = this.$('#storeSidebarErrorText');
      this.countEl = this.$('#storeCount');
      this.tabs = this.$$('[data-ss-store-tab]');
      this.closeBtns = this.$$('[data-ss-store-sidebar-close]');
      this.storeBox = this.$('[data-store-box]');
      this.storeMessage = this.storeBox ? this.storeBox.querySelector('.store-message') : null;
      this.viewLink = this.storeBox ? this.storeBox.querySelector('.view-link') : null;
      this.deliveryBox = this.$('[data-delivery-box]');
      this.endpoint = get(this.config, 'endpoints.stores', '');
      this.apiKey = get(this.config, 'apiKeys.stores', '');
      this.radiusKm = toNumber(get(this.config, 'contact.storeRadiusKm', 50), 50);
      this.mapsKey = get(this.config, 'googleMaps.apiKey', '');
      this.requireConsent = get(this.config, 'googleMaps.requireConsent', true) !== false;
      this.lastStores = null;
      this.isOpen = false;
      this.mapsLoading = false;
      this.mapsCallbacks = [];

      hide(this.overlay);
      this.overlay.setAttribute('aria-hidden', 'true');
      if (this.wrapper) {
        this.wrapper.setAttribute('role', 'dialog');
        this.wrapper.setAttribute('aria-modal', 'true');
        var heading = this.overlay.querySelector('.store-sidebar-heading');
        if (heading) { if (!heading.id) heading.id = 'ss-pdp-stores-heading'; this.wrapper.setAttribute('aria-labelledby', heading.id); }
      }
      if (this.errorText) { hide(this.errorText); this.errorText.setAttribute('role', 'alert'); }
      if (this.loader) hide(this.loader);
      if (this.list) this.list.setAttribute('aria-live', 'polite');

      this.openers.forEach((opener) => {
        if (opener.tagName === 'A' && !opener.getAttribute('href')) { opener.setAttribute('role', 'button'); opener.setAttribute('tabindex', '0'); }
        opener.setAttribute('aria-haspopup', 'dialog');
        this.on(opener, 'click', (e) => { e.preventDefault(); this.open(opener); });
        this.on(opener, 'keydown', (e) => { if (opener.tagName !== 'BUTTON' && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); this.open(opener); } });
      });
      this.closeBtns.forEach((btn) => {
        btn.setAttribute('role', 'button');
        btn.setAttribute('tabindex', '0');
        btn.setAttribute('aria-label', this.t('close'));
        this.on(btn, 'click', (e) => { e.preventDefault(); this.close(); });
        this.on(btn, 'keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.close(); } });
      });
      this.on(this.overlay, 'click', (e) => {
        if (this.root.booking && this.root.booking.isOpen) return;
        if (e.target.closest('.gm-style') || e.target.closest('#mapCanvas') || e.target.closest('.next-day-widget-modal')) return;
        if (this.wrapper && !this.wrapper.contains(e.target)) this.close();
      });

      var tabList = this.$('.store-sidebar-tabs');
      if (tabList) tabList.setAttribute('role', 'tablist');
      this.tabs.forEach((tab) => {
        tab.setAttribute('role', 'tab');
        tab.setAttribute('tabindex', '0');
        var name = tab.dataset.ssStoreTab;
        if (name === 'map' && !this.mapsKey) { hide(tab); return; }
        this.on(tab, 'click', () => this.setTab(name));
        this.on(tab, 'keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.setTab(name); } });
      });
      if (this.list) { this.list.setAttribute('role', 'tabpanel'); }
      if (this.mapWrap) { this.mapWrap.setAttribute('role', 'tabpanel'); if (!this.mapsKey) hide(this.mapWrap); }
      this.setTab('list');

      if (this.input) {
        var maxLength = toNumber(get(this.config, 'postalCode.maxLength', 0), 0);
        if (maxLength > 0) this.input.setAttribute('maxlength', String(maxLength));
        this.input.setAttribute('autocomplete', 'postal-code');
        this.on(this.input, 'keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); this.onCheck(); } });
      }
      if (this.checkBtn) {
        this.checkBtn.setAttribute('type', 'button');
        this.on(this.checkBtn, 'click', (e) => { e.preventDefault(); this.onCheck(); });
      }

      // Card interactions (delegated; cards are re-rendered)
      if (this.list) {
        this.on(this.list, 'click', (e) => {
          var book = e.target.closest('[data-ss-book-store], .book-btn');
          if (book) {
            e.preventDefault();
            this.track('book_visit', { legacy: ['pincode_checker', book.dataset.storeLoctitle || ''], store: book.dataset.storeLoctitle || '' });
            if (this.root.booking) this.root.booking.open(book.dataset.storeLoctitle || '', book.dataset.storeLocid || '', book.dataset.storeJson || '', book);
            return;
          }
          var call = e.target.closest('.contact-info-callus-btn');
          if (call) this.track('call_us', { legacy: ['pincode_checker'] });
          var visit = e.target.closest('.contact-info-storevisit-btn');
          if (visit) this.track('store_visit', { legacy: ['pincode_checker', visit.getAttribute('href') || ''] });
        });
      }

      if (this.storeBox && !this.endpoint) hide(this.storeBox);
      if (this.viewLink && !this.endpoint) hide(this.viewLink);
    }

    savedCode() {
      if (this.root.delivery && typeof this.root.delivery.loadCode === 'function') return this.root.delivery.loadCode();
      var v = storage.get(get(this.config, 'postalCode.storageKey', 'ssPdpPostalCode'));
      return typeof v === 'string' ? v : null;
    }

    isValidCode(code) {
      if (this.root.delivery && typeof this.root.delivery.isValid === 'function') return this.root.delivery.isValid(code);
      try { return new RegExp(get(this.config, 'postalCode.pattern', '^.{3,10}$')).test(String(code || '').trim()); } catch (e) { return !!code; }
    }

    /* ---- open / close / tabs ---- */

    open(trigger) {
      if (!this.overlay || this.isOpen) return;
      this.isOpen = true;
      var saved = this.savedCode();
      if (this.input && saved) this.input.value = saved;
      this.track('view_stores', { legacy: ['pincode_checker'] });
      show(this.overlay);
      this.overlay.classList.add('is-open');
      this.overlay.setAttribute('aria-hidden', 'false');
      if (this.wrapper) { this.wrapper.classList.remove('slide-out'); this.wrapper.classList.add('slide-in'); }
      scrollLock.lock();
      this.root.layers.push({ container: this.wrapper || this.overlay, onClose: () => this.close(), returnFocus: trigger || document.activeElement, initialFocus: this.input || null });
      this.root.dispatchEvent(new CustomEvent('ss-pdp:layer', { detail: { name: 'stores', open: true } }));
      if (saved && this.endpoint) {
        if (this.list) this.list.innerHTML = '';
        this.fetchStores(saved, false);
      } else if (!this.endpoint && this.list) {
        this.renderEmpty(this.t('storeCheckerUnavailable'));
      }
    }

    close() {
      if (!this.overlay || !this.isOpen) return;
      this.isOpen = false;
      if (this.input) this.input.value = '';
      if (this.wrapper) { this.wrapper.classList.remove('slide-in'); this.wrapper.classList.add('slide-out'); }
      this.overlay.classList.remove('is-open');
      this.overlay.setAttribute('aria-hidden', 'true');
      this.root.layers.remove(this.wrapper || this.overlay);
      this.setTimeout(() => {
        hide(this.overlay);
        scrollLock.unlock();
      }, 300);
      this.root.dispatchEvent(new CustomEvent('ss-pdp:layer', { detail: { name: 'stores', open: false } }));
    }

    setTab(name) {
      var tab = name === 'map' && this.mapsKey ? 'map' : 'list';
      this.tabs.forEach((t) => {
        var active = t.dataset.ssStoreTab === tab;
        t.classList.toggle('active', active);
        t.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      if (this.list) show(this.list, tab === 'list');
      if (this.mapWrap) show(this.mapWrap, tab === 'map');
      if (tab === 'map') this.renderMapTab();
    }

    onCheck() {
      var code = this.input ? this.input.value.trim() : '';
      this.$$('.custom-pincode-container .next-day-widget-banner').forEach((w) => hide(w));
      if (!this.isValidCode(code)) { this.showSidebarError(this.t('invalidPostalCode')); return; }
      this.setLoading(true);
      if (this.root.delivery && this.root.delivery.enabled) {
        this.root.delivery.handleEDD(code, { fromSidebar: true }).then(() => this.setLoading(false));
      } else {
        this.fetchStores(code, false).then(() => this.setLoading(false));
      }
    }

    setLoading(loading) {
      if (this.checkBtn) show(this.checkBtn, !loading);
      if (this.loader) show(this.loader, loading);
    }

    showSidebarError(message) {
      if (!this.errorText) return;
      this.errorText.textContent = message;
      show(this.errorText);
      this.setLoading(false);
      this.setTimeout(() => hide(this.errorText), 5000);
    }

    renderEmpty(message) {
      if (!this.list) return;
      this.list.innerHTML = '<div class="ss-pdp__empty-state">' + escapeHtml(message) + '</div>';
    }

    /* ---- data ---- */

    storesUrl(code, withProduct) {
      var sep = this.endpoint.indexOf('?') === -1 ? '?' : '&';
      var url = this.endpoint + sep + 'postalCode=' + encodeURIComponent(code) + '&countryCode=' + encodeURIComponent(this.config.country || '');
      if (withProduct) url += '&productId=' + encodeURIComponent(String(get(this.config, 'product.id', '')));
      return url;
    }

    headers() {
      var h = { 'Accept': 'application/json' };
      if (this.apiKey) h['X-Api-Key'] = this.apiKey;
      return h;
    }

    fetchStores(code, isFallback) {
      if (!this.endpoint) { if (this.storeBox) hide(this.storeBox); return Promise.resolve([]); }
      if (!this.isValidCode(code)) { this.showSidebarError(this.t('invalidPostalCode')); return Promise.resolve([]); }
      if (this.list && this.isOpen) this.list.innerHTML = '<div class="store-sidebar-fetch-spinner" role="status" aria-label="' + escapeAttr(this.t('loading')) + '"><div class="store-sidebar-fetch-spinner-circle"></div></div>';
      var byDistance = (a, b) => toNumber(a.distance, 0) - toNumber(b.distance, 0);
      return request(this.storesUrl(code, true), { headers: this.headers() })
        .then((res) => {
          var all = Array.isArray(get(res, 'data.stores', null)) ? res.data.stores : (Array.isArray(res.data) ? res.data : []);
          var stores = all.filter((s) => s.isProductAvailable !== false && toNumber(s.distance, Infinity) <= this.radiusKm && !s.comingSoon).sort(byDistance);
          if (stores.length) return this.processStores(stores, false);
          return request(this.storesUrl(code, false), { headers: this.headers() }).then((res2) => {
            var all2 = Array.isArray(get(res2, 'data.stores', null)) ? res2.data.stores : (Array.isArray(res2.data) ? res2.data : []);
            var fallback = all2.filter((s) => toNumber(s.distance, Infinity) <= this.radiusKm * 2 && !s.comingSoon).sort(byDistance);
            return this.processStores(fallback, true);
          });
        })
        .catch((err) => {
          warn('stores', err);
          this.renderEmpty(this.t('storesLoadError'));
          return [];
        });
    }

    processStores(stores, isFallback) {
      if (this.countEl) this.countEl.textContent = isFallback ? this.t('storesAvailableAt') : this.t('storeCountNear', { count: stores.length });
      this.lastStores = stores;
      if (!stores.length) {
        if (this.storeBox) hide(this.storeBox);
        if (this.deliveryBox) this.deliveryBox.classList.remove('has-store-box');
        this.renderEmpty(this.t('noStoresFound'));
        if (this.mapCanvas) this.mapCanvas.innerHTML = '<div class="ss-pdp__empty-state">' + escapeHtml(this.t('noStoresOnMap')) + '</div>';
        return stores;
      }
      if (this.deliveryBox) this.deliveryBox.classList.add('has-store-box');
      if (this.storeBox) show(this.storeBox);
      if (this.storeMessage) {
        this.storeMessage.textContent = isFallback ? this.t('storesAvailableAt') : (stores.length === 1 ? this.t('tryAtStoreOne') : this.t('tryAtStoreOther', { count: stores.length }));
      }
      if (this.viewLink) show(this.viewLink);
      if (this.list) {
        this.list.innerHTML = '';
        stores.forEach((store, index) => {
          if (store.comingSoon) return;
          this.list.appendChild(this.renderCard(store));
          if (index < stores.length - 1) {
            var sep = document.createElement('div');
            sep.className = 'store-separator';
            this.list.appendChild(sep);
          }
        });
      }
      if (this.mapsKey && this.mapWrap && isVisible(this.mapWrap)) this.renderMapTab();
      return stores;
    }

    isStoreOpenNow(timings) {
      var range = parseTimeRange(timings);
      if (!range) return { isOpenToday: false, formattedTimings: this.t('closed') };
      var now = new Date();
      var current = now.getHours() * 60 + now.getMinutes();
      var formatted = formatClockTime(ssLocaleTag(this.config), range.open) + ' - ' + formatClockTime(ssLocaleTag(this.config), range.close);
      return { isOpenToday: current < range.close.totalMinutes, formattedTimings: formatted, openTime: range.open.totalMinutes, closeTime: range.close.totalMinutes };
    }

    directionsUrl(store) {
      var link = safeUrl(store.mapLink || store.googleMapsUrl || '');
      if (link) return link;
      var lat = parseFloat(store.latitude), lng = parseFloat(store.longitude);
      if (Number.isFinite(lat) && Number.isFinite(lng)) return 'https://www.google.com/maps?q=' + lat + ',' + lng;
      return '';
    }

    renderCard(store) {
      var open = this.isStoreOpenNow(store.storeTimings);
      var openText = open.isOpenToday ? this.t('openToday') : this.t('openTomorrow');
      var openClass = open.isOpenToday ? 'Today' : 'Tomorrow';
      var parking = String(store.parking || '').toLowerCase();
      var parkingLabel = parking.indexOf('valet') !== -1 ? this.t('valetParking') : (parking ? this.t('parkingAvailable') : '');
      var phone = String(store.contact || store.phone || '').trim();
      var telHref = phone ? 'tel:' + phone.replace(/[^\d+]/g, '') : '';
      var directions = this.directionsUrl(store);
      var distance = Number.isFinite(parseFloat(store.distance)) ? this.t('distanceKm', { distance: this.formatNumber(Math.round(parseFloat(store.distance)), 0) }) : '';
      var storeJson = '';
      try { storeJson = encodeURIComponent(JSON.stringify({ storeName: store.storeName, address: store.address, mapLink: directions, storeId: store.storeId })); } catch (e) { storeJson = ''; }

      var el = document.createElement('div');
      el.className = 'store-card';
      el.innerHTML =
        '<div class="store-title">' + escapeHtml(store.storeName) + '</div>' +
        '<p class="store-address">' + escapeHtml([store.storeShortCode, store.city].filter(Boolean).join(' - ') || store.address || '') + '</p>' +
        '<div class="store-meta-address-strip">' +
          '<div class="store-meta-top">' +
            '<div class="store-timings"><span class="store-open-status ' + openClass + '">' + escapeHtml(openText) + ': </span><span class="store-time-text">' + escapeHtml(open.formattedTimings) + '</span></div>' +
            (distance ? '<div class="store-distance"><span>' + escapeHtml(distance) + '</span></div>' : '') +
          '</div>' +
          '<p class="store-address-meta-strip">' +
            '<span class="store-open-status Today">' + escapeHtml(this.t('timingsLabel')) + ' </span><span class="store-time-text">' + escapeHtml(store.storeTimings || '') + '</span>' +
            (parkingLabel ? '<span class="store-open-status Today store-parking-text">' + escapeHtml(parkingLabel) + '</span>' : '') +
          '</p>' +
        '</div>' +
        '<div class="store-contact-row">' +
          (telHref ? '<div class="contact-info"><a class="contact-info-callus-btn" href="' + escapeAttr(telHref) + '">' + escapeHtml(phone) + '</a></div>' : '') +
          (directions ? '<div class="contact-info"><a class="contact-info-storevisit-btn" href="' + escapeAttr(directions) + '" target="_blank" rel="noopener">' + escapeHtml(this.t('getLocation')) + '</a></div>' : '') +
        '</div>' +
        '<button type="button" class="book-btn" data-ss-book-store data-store-locid="' + escapeAttr(store.storeId || '') + '" data-store-loctitle="' + escapeAttr(store.storeName || '') + '" data-store-json="' + escapeAttr(storeJson) + '">' + escapeHtml(this.t('bookAppointment')) + '</button>';
      return el;
    }

    /* ---- map with two-click consent ---- */

    hasConsent() {
      if (!this.requireConsent) return true;
      return sessionStore.get(MAP_CONSENT_KEY) === true || sessionStore.get(MAP_CONSENT_KEY) === '1';
    }

    renderMapTab() {
      if (!this.mapCanvas || !this.mapsKey) return;
      if (!this.lastStores || !this.lastStores.length) {
        this.mapCanvas.innerHTML = '<div class="ss-pdp__empty-state">' + escapeHtml(this.t('noStoresOnMap')) + '</div>';
        return;
      }
      if (!this.hasConsent()) { this.renderConsent(); return; }
      this.loadMap(this.lastStores);
    }

    renderConsent() {
      this.mapCanvas.innerHTML = '';
      var box = document.createElement('div');
      box.className = 'ss-pdp__map-consent';
      var text = document.createElement('p');
      text.textContent = this.t('mapConsentText');
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'button';
      btn.textContent = this.t('mapConsentButton');
      box.appendChild(text);
      box.appendChild(btn);
      this.mapCanvas.appendChild(box);
      this.on(btn, 'click', () => {
        sessionStore.set(MAP_CONSENT_KEY, true);
        this.track('map_consent', { legacy: [] });
        this.loadMap(this.lastStores);
      });
    }

    loadGoogleMapsScript(callback) {
      if (window.google && window.google.maps) { if (callback) callback(); return; }
      if (callback) this.mapsCallbacks.push(callback);
      if (this.mapsLoading) return;
      this.mapsLoading = true;
      var script = document.createElement('script');
      script.src = 'https://maps.googleapis.com/maps/api/js?key=' + encodeURIComponent(this.mapsKey) + '&loading=async';
      script.async = true;
      script.defer = true;
      script.setAttribute('data-ss-pdp-maps', '');
      var timeout = this.setTimeout(() => {
        this.mapsLoading = false;
        this.mapsCallbacks = [];
        if (this.mapCanvas) this.mapCanvas.innerHTML = '<div class="ss-pdp__empty-state">' + escapeHtml(this.t('mapLoadError')) + '</div>';
      }, REQUEST_TIMEOUT_MS);
      script.onload = () => {
        clearTimeout(timeout);
        this.mapsLoading = false;
        var waitForMaps = (attempts) => {
          if (window.google && window.google.maps && window.google.maps.Map) {
            var cbs = this.mapsCallbacks.slice(); this.mapsCallbacks = [];
            cbs.forEach((cb) => { try { cb(); } catch (e) { warn('map callback', e); } });
          } else if (attempts > 0) {
            this.setTimeout(() => waitForMaps(attempts - 1), 100);
          }
        };
        waitForMaps(50);
      };
      script.onerror = () => {
        clearTimeout(timeout);
        this.mapsLoading = false;
        this.mapsCallbacks = [];
        warn('Failed to load Google Maps script');
        if (this.mapCanvas) this.mapCanvas.innerHTML = '<div class="ss-pdp__empty-state">' + escapeHtml(this.t('mapLoadError')) + '</div>';
      };
      document.head.appendChild(script);
    }

    loadMap(stores) {
      if (!this.mapCanvas) return;
      if (!stores || !stores.length) {
        this.mapCanvas.innerHTML = '<div class="ss-pdp__empty-state">' + escapeHtml(this.t('noStoresOnMap')) + '</div>';
        return;
      }
      if (!(window.google && window.google.maps && window.google.maps.Map)) {
        this.mapCanvas.innerHTML = '<div class="ss-pdp__empty-state" role="status">' + escapeHtml(this.t('mapLoading')) + '</div>';
        this.loadGoogleMapsScript(() => this.loadMap(stores));
        return;
      }
      var withCoords = stores.filter((s) => Number.isFinite(parseFloat(s.latitude)) && Number.isFinite(parseFloat(s.longitude)));
      if (!withCoords.length) {
        this.mapCanvas.innerHTML = '<div class="ss-pdp__empty-state">' + escapeHtml(this.t('noStoresOnMap')) + '</div>';
        return;
      }
      this.mapCanvas.innerHTML = '';
      var maps = window.google.maps;
      var map = new maps.Map(this.mapCanvas, {
        center: { lat: parseFloat(withCoords[0].latitude), lng: parseFloat(withCoords[0].longitude) },
        zoom: 10
      });
      var bounds = new maps.LatLngBounds();
      var markerIcon = safeUrl(get(this.config, 'googleMaps.markerIcon', ''));
      withCoords.forEach((store) => {
        var lat = parseFloat(store.latitude), lng = parseFloat(store.longitude);
        var directions = this.directionsUrl(store);
        var content =
          '<div class="ss-pdp__map-info">' +
            '<div class="ss-pdp__map-info-title">' + escapeHtml(store.storeName) + '</div>' +
            '<div class="ss-pdp__map-info-address">' + escapeHtml(store.address || '') + '</div>' +
            (directions ? '<a href="' + escapeAttr(directions) + '" target="_blank" rel="noopener">' + escapeHtml(this.t('openInGoogleMaps')) + '</a>' : '') +
          '</div>';
        var infowindow = new maps.InfoWindow({ content: content });
        var markerOptions = { position: { lat: lat, lng: lng }, map: map, title: String(store.storeName || '') };
        if (markerIcon) markerOptions.icon = { url: markerIcon, scaledSize: new maps.Size(36, 36) };
        var marker = new maps.Marker(markerOptions);
        marker.addListener('click', () => infowindow.open({ anchor: marker, map: map }));
        bounds.extend(markerOptions.position);
      });
      if (withCoords.length > 1) map.fitBounds(bounds);
    }

    destroy() {
      super.destroy();
      if (this.isOpen) {
        this.isOpen = false;
        hide(this.overlay);
        this.root.layers.remove(this.wrapper || this.overlay);
        scrollLock.unlock();
      }
    }
  }

  /* ------------------------------------------------------------------ */
  /* 14. Store booking: date/time picker, validation, consent, submit    */
  /* ------------------------------------------------------------------ */

  class StoreAppDateTimePicker {
    constructor(module, dateSelect, timeSelect, dateError, timeError) {
      this.module = module;
      this.t = module.t;
      this.config = module.config;
      this.dateSelect = dateSelect;
      this.timeSelect = timeSelect;
      this.dateError = dateError;
      this.timeError = timeError;
      this.selectedDate = null;
      this.selectedTime = null;
      this.init();
    }
    init() {
      if (!this.dateSelect || !this.timeSelect) return;
      this.generateDateOptions();
      this.generateTimeOptions();
      this.attachEventListeners();
    }
    generateDateOptions() {
      var today = startOfDay(new Date());
      var days = Math.max(1, toNumber(get(this.config, 'booking.daysAhead', 15), 15));
      this.dateSelect.innerHTML = '';
      var placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = this.t('selectDate');
      this.dateSelect.appendChild(placeholder);
      for (var i = 0; i < days; i++) {
        var date = new Date(today.getTime());
        date.setDate(today.getDate() + i);
        var option = document.createElement('option');
        option.value = date.getFullYear() + '-' + String(date.getMonth() + 1).padStart(2, '0') + '-' + String(date.getDate()).padStart(2, '0');
        option.textContent = this.formatDate(date);
        this.dateSelect.appendChild(option);
      }
    }
    generateTimeOptions() {
      var slots = get(this.config, 'booking.timeSlots', []);
      if (!Array.isArray(slots)) slots = [];
      this.timeSelect.innerHTML = '';
      var placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = this.t('selectTimeSlot');
      this.timeSelect.appendChild(placeholder);
      slots.forEach((slot) => {
        var value = String(slot || '').trim();
        if (!value) return;
        var option = document.createElement('option');
        option.value = value;
        option.textContent = value;
        this.timeSelect.appendChild(option);
      });
    }
    formatDate(date) {
      return this.module.formatDate(date, { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
    }
    /** Start time of a slot ("10:00 - 11:00" / "11:00 AM - 12:00 PM") on baseDate. */
    convertTimeToDate(timeString, baseDate) {
      var range = parseTimeRange(timeString);
      var start = range ? range.open : parseClockTime(timeString);
      if (!start) return null;
      var d = new Date(baseDate.getTime());
      d.setHours(start.hours, start.minutes, 0, 0);
      return d;
    }
    updateTimeSlotAvailability() {
      var options = Array.from(this.timeSelect.options);
      if (!this.selectedDate) {
        options.forEach((o, idx) => { if (idx > 0) { o.disabled = false; o.textContent = o.value; } });
        return;
      }
      var now = new Date();
      var isToday = this.selectedDate.toDateString() === now.toDateString();
      options.forEach((option, idx) => {
        if (idx === 0) return;
        var isPast = false;
        if (isToday) {
          var slotDate = this.convertTimeToDate(option.value, this.selectedDate);
          isPast = !!slotDate && slotDate <= now;
        }
        option.disabled = isPast;
        option.textContent = isPast ? option.value + ' ' + this.t('pastSlot') : option.value;
      });
      if (this.timeSelect.value && this.timeSelect.selectedOptions[0] && this.timeSelect.selectedOptions[0].disabled) {
        this.timeSelect.value = '';
        this.selectedTime = null;
      }
    }
    showError(element, errorElement, message) {
      if (element) { element.classList.add('error'); element.setAttribute('aria-invalid', 'true'); }
      if (errorElement) { errorElement.textContent = message; show(errorElement); }
    }
    clearError(element, errorElement) {
      if (element) { element.classList.remove('error'); element.setAttribute('aria-invalid', 'false'); }
      if (errorElement) hide(errorElement);
    }
    validateDate() {
      if (!this.dateSelect.value) { this.showError(this.dateSelect, this.dateError, this.t('dateRequired')); return false; }
      this.clearError(this.dateSelect, this.dateError);
      return true;
    }
    validateTime() {
      if (!this.timeSelect.value) { this.showError(this.timeSelect, this.timeError, this.t('timeRequired')); return false; }
      if (this.selectedDate && this.selectedTime) {
        var dt = this.convertTimeToDate(this.selectedTime, this.selectedDate);
        if (dt && dt <= new Date()) { this.showError(this.timeSelect, this.timeError, this.t('slotInPast')); return false; }
      }
      this.clearError(this.timeSelect, this.timeError);
      return true;
    }
    validateBoth() {
      var d = this.validateDate();
      var t = this.validateTime();
      return d && t;
    }
    attachEventListeners() {
      this.module.on(this.dateSelect, 'change', (e) => {
        this.selectedDate = e.target.value ? toLocalDate(e.target.value) : null;
        if (this.selectedDate) this.clearError(this.dateSelect, this.dateError);
        this.updateTimeSlotAvailability();
      });
      this.module.on(this.timeSelect, 'change', (e) => {
        this.selectedTime = e.target.value || null;
        if (this.selectedTime) this.clearError(this.timeSelect, this.timeError);
      });
      this.module.on(this.dateSelect, 'blur', () => this.validateDate());
      this.module.on(this.timeSelect, 'blur', () => this.validateTime());
    }
    getSelectedValues() {
      return {
        date: this.dateSelect.value,
        time: this.timeSelect.value,
        dateFormatted: this.selectedDate ? this.formatDate(this.selectedDate) : null
      };
    }
    reset() {
      this.selectedDate = null;
      this.selectedTime = null;
      this.clearError(this.dateSelect, this.dateError);
      this.clearError(this.timeSelect, this.timeError);
      this.updateTimeSlotAvailability();
    }
    validate() { return this.validateBoth(); }
  }

  class SsPdpBooking extends SsPdpModule {
    init() {
      this.overlay = this.$('#storeBookingOverlay');
      if (!this.overlay) return;
      this.formContainer = this.$('#storeBookingFormContainer');
      this.successContainer = this.$('#storeSuccessContainer');
      this.form = this.$('#storeBookingForm');
      this.submitBtn = this.$('#storeSubmitButton');
      this.loader = this.$('#storeFormLoader');
      this.nameInput = this.$('#store-customer-name');
      this.phoneInput = this.$('#store-customer-phone');
      this.phoneAlert = this.$('#store-phone-alert');
      this.consent = this.$('#store-consent');
      this.locationInput = this.$('#store-selected-location');
      this.idInput = this.$('#store-selected-id');
      this.closeBtns = this.$$('[data-ss-booking-close]');
      this.leadUrl = get(this.config, 'endpoints.lead', '');
      this.whatsappUrl = get(this.config, 'endpoints.whatsappNotify', '');
      this.formUrl = get(this.config, 'endpoints.bookingForm', '');
      try { this.phonePattern = new RegExp(get(this.config, 'phone.pattern', '^\\+?[0-9 ()\\-]{6,20}$')); } catch (e) { this.phonePattern = /^\+?[0-9 ()\-]{6,20}$/; }
      this.isOpen = false;
      this.store = null;

      hide(this.overlay);
      this.overlay.setAttribute('aria-hidden', 'true');
      if (this.formContainer) {
        this.formContainer.setAttribute('role', 'dialog');
        this.formContainer.setAttribute('aria-modal', 'true');
        var header = this.overlay.querySelector('.store-booking-header');
        if (header) { if (!header.id) header.id = 'ss-pdp-booking-heading'; this.formContainer.setAttribute('aria-labelledby', header.id); }
      }
      if (this.successContainer) { hide(this.successContainer); this.successContainer.setAttribute('role', 'dialog'); this.successContainer.setAttribute('aria-modal', 'true'); }
      if (this.loader) hide(this.loader);
      ['store-name-error', 'store-phone-error', 'store-phone-alert', 'storeAppDateError', 'storeAppTimeError', 'store-consent-error'].forEach((id) => {
        var el = this.$('#' + id);
        if (el) { hide(el); el.setAttribute('role', 'alert'); }
      });

      var currentUrl = this.$('#store-current-url');
      if (currentUrl) currentUrl.value = window.location.href;
      var sourceField = this.$('#store-source-field');
      if (sourceField) sourceField.value = getUrlParameter('utm_medium') || get(this.config, 'booking.source', 'Organic');
      var leadStatus = this.$('#store-lead-status');
      if (leadStatus) leadStatus.value = get(this.config, 'booking.leadStatus', 'Store');

      this.picker = new StoreAppDateTimePicker(this, this.$('#store-app-date-select'), this.$('#store-app-time-select'), this.$('#storeAppDateError'), this.$('#storeAppTimeError'));

      if (this.nameInput) {
        this.on(this.nameInput, 'input', (e) => {
          var cleaned = e.target.value.replace(/[^\p{L}\p{M}\s'’.\-]/gu, '');
          if (cleaned !== e.target.value) e.target.value = cleaned;
          this.clearError('name');
        });
      }
      if (this.phoneInput) {
        var maxLength = toNumber(get(this.config, 'phone.maxLength', 0), 0);
        if (maxLength > 0) this.phoneInput.setAttribute('maxlength', String(maxLength));
        this.phoneInput.setAttribute('inputmode', 'tel');
        this.phoneInput.setAttribute('autocomplete', 'tel');
        this.on(this.phoneInput, 'input', (e) => {
          var cleaned = e.target.value.replace(/[^0-9+()\-\s]/g, '');
          if (cleaned !== e.target.value) e.target.value = cleaned;
          this.validatePhoneLive();
          this.clearError('phone');
        });
      }
      if (this.consent) {
        this.consent.setAttribute('aria-required', 'true');
        this.on(this.consent, 'change', () => this.clearError('consent'));
      }
      this.closeBtns.forEach((btn) => {
        btn.setAttribute('role', 'button');
        btn.setAttribute('tabindex', '0');
        if (!btn.textContent.trim() || btn.textContent.trim() === '×') btn.setAttribute('aria-label', this.t('close'));
        this.on(btn, 'click', (e) => { e.preventDefault(); this.close(); });
        this.on(btn, 'keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.close(); } });
      });
      this.on(this.overlay, 'click', (e) => { if (e.target === this.overlay) this.close(); });
      if (this.form) this.on(this.form, 'submit', (e) => { e.preventDefault(); this.submit(); });
    }

    showError(field, message) {
      var el = this.$('#store-' + field + '-error');
      if (el) { el.textContent = message; show(el); }
      var input = field === "name" ? this.nameInput : field === "phone" ? this.phoneInput : field === "consent" ? this.consent : null;
      if (input) input.setAttribute("aria-invalid", "true");
      if (field === "consent" && this.consent) { var row = this.consent.closest(".ss-pdp__consent-row"); if (row) row.classList.add("is-invalid"); }
    }
    clearError(field) {
      var el = this.$('#store-' + field + '-error');
      if (el) hide(el);
      var input = field === "name" ? this.nameInput : field === "phone" ? this.phoneInput : field === "consent" ? this.consent : null;
      if (input) input.setAttribute("aria-invalid", "false");
      if (field === "consent" && this.consent) { var row = this.consent.closest(".ss-pdp__consent-row"); if (row) row.classList.remove("is-invalid"); }
    }
    validatePhoneLive() {
      if (!this.phoneInput || !this.phoneAlert) return;
      var value = this.phoneInput.value.trim();
      var invalid = value !== '' && !this.phonePattern.test(value);
      this.phoneAlert.textContent = invalid ? this.t('phoneInvalid') : '';
      show(this.phoneAlert, invalid);
      if (this.submitBtn) this.submitBtn.disabled = invalid;
    }
    validateForm() {
      var valid = true;
      var name = this.nameInput ? this.nameInput.value.trim() : '';
      var phone = this.phoneInput ? this.phoneInput.value.trim() : '';
      if (!name) { this.showError('name', this.t('nameRequired')); valid = false; }
      if (!phone) { this.showError('phone', this.t('phoneRequired')); valid = false; }
      else if (!this.phonePattern.test(phone)) { this.showError('phone', this.t('phoneInvalid')); valid = false; }
      if (!this.consent || !this.consent.checked) {
        this.showError('consent', this.t('consentRequired'));
        if (!this.$('#store-consent-error')) this.root.toast.show(this.t('consentRequired'), 'error');
        valid = false;
      }
      return valid;
    }

    open(storeName, storeId, storeObjStr, trigger) {
      if (!this.overlay) return;
      this.store = null;
      if (storeObjStr) {
        try { this.store = JSON.parse(decodeURIComponent(storeObjStr)); } catch (e) { this.store = null; }
      }
      if (this.locationInput) this.locationInput.value = storeName || '';
      if (this.idInput) this.idInput.value = storeId || '';
      show(this.overlay);
      this.overlay.classList.add('is-open');
      this.overlay.setAttribute('aria-hidden', 'false');
      if (this.formContainer) show(this.formContainer);
      if (this.successContainer) hide(this.successContainer);
      this.isOpen = true;
      scrollLock.lock();
      this.root.layers.push({ container: this.overlay, onClose: () => this.close(), returnFocus: trigger || document.activeElement, initialFocus: this.nameInput || null });
      this.root.dispatchEvent(new CustomEvent('ss-pdp:layer', { detail: { name: 'booking', open: true } }));
    }

    close() {
      if (!this.overlay || !this.isOpen) return;
      this.isOpen = false;
      hide(this.overlay);
      this.overlay.classList.remove('is-open');
      this.overlay.setAttribute('aria-hidden', 'true');
      if (this.form) this.form.reset();
      if (this.picker) this.picker.reset();
      ['name', 'phone', 'consent'].forEach((f) => this.clearError(f));
      if (this.phoneAlert) hide(this.phoneAlert);
      if (this.submitBtn) this.submitBtn.disabled = false;
      if (this.formContainer) show(this.formContainer);
      if (this.successContainer) hide(this.successContainer);
      scrollLock.unlock();
      this.root.layers.remove(this.overlay);
      this.root.dispatchEvent(new CustomEvent('ss-pdp:layer', { detail: { name: 'booking', open: false } }));
    }

    setSubmitting(busy) {
      if (this.submitBtn) { this.submitBtn.disabled = busy; this.submitBtn.setAttribute('aria-busy', busy ? 'true' : 'false'); }
      if (this.loader) show(this.loader, busy);
    }

    showSuccess() {
      if (this.form) this.form.reset();
      if (this.picker) this.picker.reset();
      if (this.formContainer) hide(this.formContainer);
      if (this.successContainer) {
        show(this.successContainer);
        var focusTarget = this.successContainer.querySelector('button, [data-ss-booking-close]');
        if (focusTarget) try { focusTarget.focus(); } catch (e) { /* noop */ }
      }
    }

    buildLeadPayload(formData, dateText, timeText) {
      var now = new Date();
      var activityNote = '| Date: ' + dateText + ' | Time Slot: ' + timeText + ' | Booking Stamp: ' + now.toISOString();
      var variant = this.root.currentVariant || {};
      return {
        activity_note: activityNote,
        activity_source: 'Appointment Booked',
        source: 'Appointment Booked',
        lead_status: get(this.config, 'booking.leadStatus', 'Store'),
        utm_source: formData.get('source') || get(this.config, 'booking.source', 'Organic'),
        source_page: window.location.href,
        user_name: formData.get('name') || '',
        user_email: formData.get('email') || '',
        phone: formData.get('phone') || '',
        postal_code: this.root.delivery && typeof this.root.delivery.loadCode === 'function' ? (this.root.delivery.loadCode() || '') : '',
        country: this.config.country || '',
        locale: this.config.locale || '',
        store_id: formData.get('store-id') || '',
        store_name: formData.get('store-location') || '',
        store_address: this.store && this.store.address ? this.store.address : '',
        appointment_date: formData.get('appointment-date') || '',
        appointment_date_label: dateText,
        time_slot: timeText,
        product_id: get(this.config, 'product.id', ''),
        product_title: get(this.config, 'product.title', ''),
        product_type: get(this.config, 'product.type', ''),
        variant_id: variant.id || '',
        user_agent: navigator.userAgent || '',
        consent: true,
        consentTimestamp: now.toISOString()
      };
    }

    submit() {
      if (!this.form) return;
      if (this.picker && !this.picker.validate()) return;
      if (!this.validateForm()) return;

      var formData = new FormData(this.form);
      var dateSelect = this.$('#store-app-date-select');
      var timeSelect = this.$('#store-app-time-select');
      var dateText = dateSelect && dateSelect.selectedOptions[0] ? dateSelect.selectedOptions[0].textContent : '';
      var timeText = timeSelect && timeSelect.value ? timeSelect.value : '';
      formData.set('consent', 'true');
      formData.set('consent_timestamp', new Date().toISOString());

      this.track('book_visit_submit', { legacy: ['pincode_checker', '', '', '', '', formData.get('store-location') || ''], store: formData.get('store-location') || '' });

      if (!this.leadUrl && !this.formUrl && !safeUrl(this.form.getAttribute('action') || '')) {
        this.showError('consent', this.t('bookingNotConfigured'));
        this.root.toast.show(this.t('bookingNotConfigured'), 'error');
        return;
      }

      this.setSubmitting(true);
      var chain;
      if (this.leadUrl) {
        chain = request(this.leadUrl, { method: 'POST', headers: jsonHeaders(), body: JSON.stringify(this.buildLeadPayload(formData, dateText, timeText)) })
          .then((res) => {
            if (!res.ok) throw new Error('Lead API ' + res.status);
            if (this.whatsappUrl) {
              var raw = JSON.stringify({
                template_attributes: {
                  templateName: 'Storevisit',
                  storeName: formData.get('store-location') || '',
                  bookingDate: dateText,
                  timeSlot: timeText,
                  storeaddress: this.store && this.store.address ? this.store.address : '',
                  googlelink: this.store && this.store.mapLink ? this.store.mapLink : '',
                  phoneNo: formData.get('phone') || ''
                }
              });
              return request(this.whatsappUrl, { method: 'POST', headers: jsonHeaders(), body: raw, parse: 'text' }).catch((e) => warn('whatsapp notify', e));
            }
            return null;
          });
      } else {
        var action = this.formUrl || safeUrl(this.form.getAttribute('action') || '');
        chain = request(action, { method: 'POST', body: formData, mode: 'no-cors' });
      }

      chain
        .then(() => { this.showSuccess(); this.track('book_visit_success', { legacy: [] }); })
        .catch((err) => {
          warn('booking submit', err);
          this.root.toast.show(this.t('bookingError'), 'error');
        })
        .finally(() => this.setSubmitting(false));
    }

    openWhatsApp(storeName) {
      var phone = String(get(this.config, 'contact.whatsapp', '') || '').replace(/[^\d]/g, '');
      if (!phone) return;
      var message = this.t('whatsappBookMessage', { store: decodeURIComponent(storeName || '') });
      var url = 'https://api.whatsapp.com/send/?phone=' + encodeURIComponent(phone) + '&text=' + encodeURIComponent(message) + '&type=phone_number&app_absent=0';
      window.open(url, '_blank', 'noopener');
    }

    destroy() {
      super.destroy();
      if (this.isOpen) this.close();
    }
  }

  /* ------------------------------------------------------------------ */
  /* 15. Next-day banner + T&C modal                                     */
  /* ------------------------------------------------------------------ */

  class SsPdpNextDay extends SsPdpModule {
    init() {
      this.modal = this.$('#nextDayWidgetModal');
      this.openers = this.$$('[data-ss-next-day-open]');
      this.banners = this.$$('.next-day-widget-banner');
      var enabled = get(this.config, 'features.nextDay', false) !== false;
      if (!enabled) {
        this.banners.forEach((b) => hide(b));
        if (this.modal) hide(this.modal);
        return;
      }
      if (!this.modal) return;
      this.modal.setAttribute('aria-hidden', 'true');
      var dialog = this.modal.querySelector('.next-day-widget-modal') || this.modal;
      dialog.setAttribute('role', 'dialog');
      dialog.setAttribute('aria-modal', 'true');
      var title = this.modal.querySelector('.next-day-widget-modal__header h3');
      if (title) { if (!title.id) title.id = 'ss-pdp-next-day-title'; dialog.setAttribute('aria-labelledby', title.id); }
      this.dialog = dialog;
      this.closeBtn = this.modal.querySelector('.next-day-widget-modal__close');
      if (this.closeBtn) { this.closeBtn.setAttribute('type', 'button'); this.closeBtn.setAttribute('aria-label', this.t('close')); this.on(this.closeBtn, 'click', (e) => { e.preventDefault(); this.close(); }); }
      this.openers.forEach((opener) => {
        opener.setAttribute('aria-haspopup', 'dialog');
        if (opener.tagName === 'A') opener.setAttribute('href', '#' + this.modal.id);
        this.on(opener, 'click', (e) => { e.preventDefault(); this.open(opener); });
      });
      this.on(this.modal, 'click', (e) => { if (e.target === this.modal) this.close(); });
      this.isOpen = false;
    }
    open(trigger) {
      if (!this.modal || this.isOpen) return;
      this.isOpen = true;
      this.modal.classList.add('active');
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('next-day-widget-no-scroll');
      this.root.layers.push({ container: this.dialog, onClose: () => this.close(), returnFocus: trigger || document.activeElement, initialFocus: this.closeBtn || null });
      this.track('next_day_tc_open', { legacy: [] });
    }
    close() {
      if (!this.modal || !this.isOpen) return;
      this.isOpen = false;
      this.modal.classList.remove('active');
      this.modal.setAttribute('aria-hidden', 'true');
      this.root.layers.remove(this.dialog);
      this.setTimeout(() => document.body.classList.remove('next-day-widget-no-scroll'), 450);
    }
    destroy() {
      super.destroy();
      document.body.classList.remove('next-day-widget-no-scroll');
      if (this.isOpen) { this.isOpen = false; this.root.layers.remove(this.dialog); }
    }
  }

  /* ------------------------------------------------------------------ */
  /* 16. Mobile sticky bar                                               */
  /* ------------------------------------------------------------------ */

  class SsPdpStickyBar extends SsPdpModule {
    init() {
      this.bar = this.$('.pv-variant-atc-wrapper.mobile-only');
      if (!this.bar) return;
      this.anchor = this.$('.mattress-pdp-form-wrap') || this.$('.mattress-pdp-quantity-wrap') || this.$$('.mattress-pdp-atc-btn').find((b) => !b.closest('#pv-pdp-variant-full-wrapper') && !this.bar.contains(b));
      this.layerOpen = false;
      this.anchorVisible = true;
      this.bar.setAttribute('aria-hidden', 'true');
      if (!this.anchor || !('IntersectionObserver' in window)) { this.anchorVisible = false; this.render(); return; }
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => { this.anchorVisible = entry.isIntersecting; });
        this.render();
      }, { threshold: 0 });
      this.observer.observe(this.anchor);
      this.on(this.root, 'ss-pdp:layer', (e) => {
        if (e.detail && e.detail.name === 'variant') { this.layerOpen = !!e.detail.open; this.render(); }
      });
    }
    render() {
      var visible = !this.anchorVisible && !this.layerOpen && isSmallScreen();
      this.bar.classList.toggle("is-hidden", !visible);
      this.bar.classList.toggle("is-visible", visible);
      this.bar.setAttribute('aria-hidden', visible ? 'false' : 'true');
    }
    destroy() {
      super.destroy();
      if (this.observer) this.observer.disconnect();
    }
  }

  /* ------------------------------------------------------------------ */
  /* 17. Store CTAs (video call / call us / visit store analytics)       */
  /* ------------------------------------------------------------------ */

  class SsPdpStoreCtas extends SsPdpModule {
    init() {
      this.$$('.secondary-btn-video-call-event[data-ss-popin], [data-ss-popin]').forEach((btn) => {
        if (typeof window.Popin !== 'function') { hide(btn); return; }
        this.on(btn, 'click', (e) => {
          e.preventDefault();
          var campaign = {};
          try { campaign = JSON.parse(btn.dataset.popinCampaign || 'null') || get(this.config, 'popin.campaign', {}) || {}; } catch (err) { campaign = get(this.config, 'popin.campaign', {}) || {}; }
          var groupId = btn.dataset.popinGroup || get(this.config, 'popin.groupId', '');
          this.track('video_call_popin', { legacy: ['product_page'] });
          try { window.Popin('open', Object.assign({ groupId: groupId }, { campaign: campaign })); } catch (err) { warn('Popin', err); }
        });
      });
      this.$$('.secondary-btn-call-us-event').forEach((btn) => this.on(btn, 'click', () => this.track('call_us', { legacy: ['product_page'] })));
      this.$$('.dev-ga-mattress-v2-store-visit-event, .asb-cta-visitstore').forEach((btn) => {
        this.on(btn, 'click', () => this.track('store_visit', { legacy: ['product_page'], location: 'pdp' }));
      });
      var phone = String(get(this.config, 'contact.phone', '') || '').trim();
      this.$$('.pv-variant-call-button, #mobilecallbutton').forEach((link) => {
        if (link.tagName !== 'A') return;
        if (phone) link.setAttribute('href', 'tel:' + phone.replace(/[^\d+]/g, ''));
        else hide(link.closest('.secondary-btn') || link);
      });
    }
  }

  /* ------------------------------------------------------------------ */
  /* 18. Recommender                                                     */
  /* ------------------------------------------------------------------ */

  class SsPdpRecommender extends SsPdpModule {
    init() {
      if (get(this.config, 'features.recommender', true) === false) {
        this.$$('.perfect-pillow-section').forEach((s) => hide(s));
        return;
      }
      var whatsapp = String(get(this.config, 'contact.whatsapp', '') || '');
      this.$$('.perfect-pillow-section .pp-btn').forEach((btn) => {
        var href = safeUrl(btn.dataset.href || '');
        var isWhatsApp = /wa\.me|whatsapp\.com/i.test(href);
        if (!href || (isWhatsApp && !whatsapp)) { hide(btn); return; }
        if (btn.tagName === 'A') btn.setAttribute('href', href);
        this.on(btn, 'click', (e) => {
          e.preventDefault();
          var section = btn.closest('.perfect-pillow-section');
          var titleEl = section ? section.querySelector('.pp-title') : null;
          this.track('recommender', { legacy: ['atc', href], title: titleEl ? titleEl.textContent.trim() : '' });
          if (isWhatsApp) window.open(href, '_blank', 'noopener');
          else window.location.href = href;
        });
      });
    }
  }

  /* ------------------------------------------------------------------ */
  /* ADDENDUM. Market-aware offers                                       */
  /* ------------------------------------------------------------------ */

  class SsPdpMarket extends SsPdpModule {
    init() {
      this.select = this.$('[data-ss-offers-country]');
      this.hint = this.$('[data-ss-market-hint]');
      this.detected = this.$('[data-ss-market-detected]');
      this.applyBtn = this.$('[data-ss-market-apply]');
      this.available = Array.isArray(get(this.config, 'market.available', null)) ? this.config.market.available.map((c) => String(c).toUpperCase()) : [];
      this.current = String(get(this.config, 'market.current', this.config.country || '') || '').toUpperCase();
      this.suggested = null;

      if (this.hint) { hide(this.hint); }
      var saved = sessionStore.get('ssPdpMarket');
      if (typeof saved === 'string' && saved && this.hasOption(saved)) this.current = saved.toUpperCase();
      if (this.select) {
        if (this.hasOption(this.current)) this.select.value = this.optionValue(this.current);
        else this.current = String(this.select.value || this.current).toUpperCase();
        this.on(this.select, 'change', () => {
          this.setMarket(this.select.value, true);
          this.track('market_change', { country: this.current });
        });
      }
      if (get(this.config, 'market.selectorEnabled', true) === false) {
        var selector = this.$('.ss-pdp__market-selector');
        if (selector) hide(selector);
      }
      if (this.applyBtn) {
        this.applyBtn.setAttribute('type', 'button');
        this.on(this.applyBtn, 'click', (e) => {
          e.preventDefault();
          if (!this.suggested) return;
          if (this.select) this.select.value = this.optionValue(this.suggested);
          this.setMarket(this.suggested, true);
          if (this.hint) hide(this.hint);
          this.track('market_apply_suggestion', { country: this.suggested });
        });
      }
      this.applyFilter(this.current);
      if (this.select) this.detectCountry();
    }

    options() { return this.select ? Array.from(this.select.options) : []; }
    hasOption(code) {
      var c = String(code || '').toUpperCase();
      return this.options().some((o) => String(o.value).toUpperCase() === c);
    }
    optionValue(code) {
      var c = String(code || '').toUpperCase();
      var o = this.options().find((x) => String(x.value).toUpperCase() === c);
      return o ? o.value : code;
    }
    optionLabel(code) {
      var c = String(code || '').toUpperCase();
      var o = this.options().find((x) => String(x.value).toUpperCase() === c);
      return o ? o.textContent.trim() : c;
    }

    setMarket(code, persist) {
      this.current = String(code || '').toUpperCase();
      if (persist) sessionStore.set('ssPdpMarket', this.current);
      this.applyFilter(this.current);
    }

    applyFilter(code) {
      var c = String(code || '').toUpperCase();
      this.$$('[data-countries]').forEach((el) => {
        var list = String(el.dataset.countries || '').split(',').map((s) => s.trim().toUpperCase()).filter(Boolean);
        var visible = !list.length || !c || list.indexOf(c) !== -1;
        if (visible) {
          el.removeAttribute('hidden');
          el.classList.remove('hidden');
          el.removeAttribute('data-market-hidden');
        } else {
          el.setAttribute('hidden', '');
          el.setAttribute('data-market-hidden', 'true');
        }
      });
      this.$$('[data-ss-bank-cards], [data-ss-payment-methods], .offer-slider-wrapper').forEach((group) => {
        var children = Array.from(group.querySelectorAll('[data-countries]'));
        if (!children.length) return;
        var anyVisible = children.some((ch) => !ch.hasAttribute('hidden'));
        show(group, anyVisible);
      });
      if (this.root.offers && typeof this.root.offers.remeasure === 'function') this.root.offers.remeasure();
      this.root.dispatchEvent(new CustomEvent('ss-pdp:market', { detail: { country: c } }));
    }

    detectCountry() {
      request('/browsing_context_suggestions.json?country[enabled]=true', { method: 'GET', headers: { 'Accept': 'application/json' }, timeout: 3000 })
        .then((res) => {
          var data = res.data || {};
          var country = get(data, 'detected_values.country', null) || get(data, 'country', null) || get(data, 'suggestions.country', null);
          if (!country) return;
          var code = String(country.handle || country.iso_code || country.isoCode || country.code || '').toUpperCase();
          if (!code || code === this.current || !this.hasOption(code)) return;
          this.suggested = code;
          if (this.detected) this.detected.textContent = country.name || this.optionLabel(code);
          if (this.hint) { show(this.hint); this.hint.setAttribute('role', 'status'); }
        })
        .catch(() => { /* ignore: suggestion is optional */ });
    }
  }

  /* ------------------------------------------------------------------ */
  /* Root custom element                                                 */
  /* ------------------------------------------------------------------ */

  var MODULES = [
    ['toast', SsPdpToast],
    ['market', SsPdpMarket],
    ['gallery', SsPdpGallery],
    ['share', SsPdpShare],
    ['wishlist', SsPdpWishlist],
    ['scrollTo', SsPdpScrollTo],
    ['price', SsPdpPrice],
    ['variantPicker', SsPdpVariantPicker],
    ['cart', SsPdpCart],
    ['offers', SsPdpOffers],
    ['timer', SsPdpOfferTimer],
    ['booking', SsPdpBooking],
    ['stores', SsPdpStores],
    ['delivery', SsPdpDelivery],
    ['nextDay', SsPdpNextDay],
    ['stickyBar', SsPdpStickyBar],
    ['storeCtas', SsPdpStoreCtas],
    ['recommender', SsPdpRecommender]
  ];

  class SsPdpMainBanner extends HTMLElement {
    constructor() {
      super();
      this._listeners = [];
      this._modules = [];
      this._initialized = false;
    }

    connectedCallback() {
      if (this._initialized) return;
      var configEl = this.querySelector('script[data-ss-pdp-config]');
      var config = parseJsonScript(configEl, null);
      if (!config || !isObject(config)) {
        if (!SsPdpMainBanner._warnedMissingConfig) {
          SsPdpMainBanner._warnedMissingConfig = true;
          warn('Missing or invalid config JSON (script[data-ss-pdp-config]); section JS disabled.');
        }
        return;
      }
      this._initialized = true;
      this.config = config;
      this.sectionId = config.sectionId || this.dataset.sectionId || '';
      this.t = createTranslator(config.strings);
      this.track = createTracker(config);
      this.formatMoney = createMoneyFormatter(config);
      this.formatNumber = createNumberFormatter(ssLocaleTag(config));
      this.formatDate = createDateFormatter(ssLocaleTag(config));
      this.layers = createLayerStack();

      var variants = parseJsonScript(this.querySelector('script[data-ss-pdp-variants]'), []);
      this.variants = Array.isArray(variants) ? variants : [];
      var customVariants = parseJsonScript(this.querySelector('script[data-ss-pdp-custom-variants]'), []);
      this.customVariants = Array.isArray(customVariants) ? customVariants : [];
      this.currentCustom = null;
      this.currentVariant = this.resolveInitialVariant();

      MODULES.forEach((entry) => {
        var name = entry[0], Ctor = entry[1];
        try {
          var instance = new Ctor(this);
          this[name] = instance;
          this._modules.push(instance);
          instance.init();
        } catch (e) {
          warn('module "' + name + '" failed to initialise', e);
        }
      });

      // Theme integration: incoming variant changes from other components.
      if (ssPubSubEvents()) {
        this._unsubscribeVariant = ssSubscribe(ssPubSubEvents().variantChange, (event) => {
          var variant = get(event, 'data.variant', null);
          if (!variant || get(event, 'data.source', '') === 'ss-pdp-main-banner') return;
          if (this.currentVariant && String(this.currentVariant.id) === String(variant.id)) return;
          var known = this.variants.find((v) => String(v.id) === String(variant.id));
          if (known) this.setVariant(known, { source: 'pubsub', silentPublish: true });
        });
      }
      this.on(document, 'variant:change', (e) => {
        if (e.target === this || this.contains(e.target)) return;
        var variant = get(e, 'detail.variant', null);
        if (!variant || (this.currentVariant && String(this.currentVariant.id) === String(variant.id))) return;
        var known = this.variants.find((v) => String(v.id) === String(variant.id));
        if (known) this.setVariant(known, { source: 'external', silentPublish: true });
      });

      // Theme editor
      this.on(document, 'shopify:section:unload', (e) => {
        if (e.detail && e.detail.sectionId && this.sectionId && String(e.detail.sectionId) === String(this.sectionId)) this.destroy();
      });
      this.on(document, 'shopify:section:load', (e) => {
        if (e.detail && e.detail.sectionId && this.sectionId && String(e.detail.sectionId) === String(this.sectionId) && !this._initialized && this.isConnected) {
          this.connectedCallback();
        }
      });

      window.SsPdpMainBanner = this;
      this.dispatchEvent(new CustomEvent('ss-pdp:variant', { detail: { variant: this.currentVariant, custom: null, source: 'init' } }));
      this.dispatchEvent(new CustomEvent('ss-pdp:ready', { bubbles: true, detail: { instance: this } }));
    }

    disconnectedCallback() { this.destroy(); }

    resolveInitialVariant() {
      var fromUrl = toNumber(getUrlParameter('variant'), 0);
      var selected = toNumber(this.config.selectedVariantId, 0);
      var pool = this.variants.concat(this.customVariants);
      var byId = (id) => id ? pool.find((v) => String(v.id) === String(id)) : null;
      return byId(fromUrl) || byId(selected) || this.variants.find((v) => v.available) || this.variants[0] || null;
    }

    /**
     * Registers a listener and remembers it for cleanup.
     */
    on(el, evt, fn, opts) {
      if (!el || typeof el.addEventListener !== 'function') return function () {};
      el.addEventListener(evt, fn, opts);
      var record = { el: el, evt: evt, fn: fn, opts: opts };
      this._listeners.push(record);
      return () => {
        el.removeEventListener(evt, fn, opts);
        this._listeners = this._listeners.filter((r) => r !== record);
      };
    }

    /**
     * Applies a variant (and optional custom-size data) and notifies everyone.
     */
    setVariant(variant, opts) {
      if (!variant) return;
      var options = opts || {};
      this.currentVariant = variant;
      this.currentCustom = options.custom || null;

      try {
        if (variant.id && window.history && typeof window.history.replaceState === 'function') {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', String(variant.id));
          window.history.replaceState({}, '', url.toString());
        }
      } catch (e) { /* noop */ }

      this.dispatchEvent(new CustomEvent('ss-pdp:variant', { detail: { variant: variant, custom: this.currentCustom, source: options.source || 'api' } }));
      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, composed: true, detail: { variant: variant, custom: this.currentCustom, sectionId: this.sectionId } }));
      if (!options.silentPublish && ssPubSubEvents()) {
        try {
          ssPublish(ssPubSubEvents().variantChange, { data: { sectionId: this.sectionId, variant: variant, source: 'ss-pdp-main-banner' } });
        } catch (e) { warn('publish variantChange', e); }
      }
    }

    destroy() {
      if (!this._initialized) return;
      this._initialized = false;
      this._modules.slice().reverse().forEach((m) => { try { m.destroy(); } catch (e) { warn('destroy', e); } });
      this._modules = [];
      MODULES.forEach((entry) => { delete this[entry[0]]; });
      this._listeners.forEach((r) => { try { r.el.removeEventListener(r.evt, r.fn, r.opts); } catch (e) { /* noop */ } });
      this._listeners = [];
      if (this.layers) this.layers.clear();
      if (typeof this._unsubscribeVariant === 'function') { this._unsubscribeVariant(); this._unsubscribeVariant = null; }
      scrollLock.reset();
      ['pv-variant-popup-open', 'next-day-widget-no-scroll', 'dev_acb-bottomsheet-open'].forEach((c) => document.body.classList.remove(c));
      if (window.SsPdpMainBanner === this) delete window.SsPdpMainBanner;
    }
  }

  SsPdpMainBanner._warnedMissingConfig = false;
  customElements.define('ss-pdp-main-banner', SsPdpMainBanner);
})();
